/*
 *  ___                  __                __  _ _
 *   | |_  _     _ |_ |_|_    _  _ _ _ _  /  \(_(_|. _  _
 *   | | )(_)|_|(_)| )|_|__)(|_)| (-_)_)  \__/| | ||| )(-
 *              _/           |
 */

/* Four functions are expected to implement a process
 *
 * 1. createState : state -> ()
 *    - create the currentProcess property of the state
 *
 * 2. processButton : (processState, { id }) -> ()
 *    - called when a button is clicked to modify the process state
 *
 * 3. processChange : (processState, { id, uid, value }) => ()
 *    - called when an input value is changes to modify the process state
 *
 * 4. processItemClick : (processState, { id, uid }) => ()
 *    - called when an item is clicked to modify the process state
 */

import {
  pages,
  otherLivesItem,
  otherLivesAcceptanceItem,
  beneficiaryItem,
  healthDetailsItem,
  healthDetailsPrefix,
  quotationPanel,
  acceptancePaymentDetails,
  additionalLifeFamilyOptions,
  additionalLifeFamilyLabels,
  optionsForAdditionalLifeRelationship,
  buttonFromAdditionalLife,
  additionalLifeDisplayRelationship,
  additionalLifeDetailsItem,
  additionalLivesDetailsTablePrefix,
  benefitSplitItem,
} from './executive-plus-revised/pages'
import {
  updateItemIn,
  createItem,
  findItemsIn,
  createButton,
  slugify,
} from '../../../src/core/dataitems'
import { form } from './executive-plus-revised/form'
import { emptyPhoneNumber } from '../../../src/core/validations'

import { cancelProcess, completeProcess } from '../../../src/core/actions'
import { original } from 'immer'
import { createSelector } from 'reselect'
import shortid from 'shortid'
import dayjs from 'dayjs'
import { validate } from './executive-plus-revised/validations'
import {
  rates as hnwRates,
  hospitalisationRates,
  transitionPlanRates,
  personalAccidentRates,
  funeralCoverRates,
  funeralExtrasRates,
  additionalLivesTransitionRates,
  oldAgeRates,
} from './executive-plus-revised/rates'

const dateFormat = 'D MMMM YYYY'

const hasValue = (value) => !!(value && value !== '')

const benefitAgeLimits = {
  death: { minEntry: 18, maxEntry: 70, maxCease: 80 },
  tpd: { minEntry: 18, maxEntry: 54, maxCease: 60 },
  ci: { minEntry: 18, maxEntry: 70, maxCease: 80 },
}

const selectedBenefitsOnQuoteScreen = (state) => {
  const b = new Set()
  const opt = state.form['benefit-option']
  if (opt === 'Accelerated') {
    b.add('death')
    const accel = state.form['accelerated-options'] || {}
    const hasTpd =
      accel['death-and-total-personal-disability'] === 'Y' ||
      accel['death-and-total-disability-and-critical-illness'] === 'Y' ||
      accel['total-personal-disability-and-critical-illness'] === 'Y'
    const hasCi =
      accel['death-and-critical-illness'] === 'Y' ||
      accel['death-and-total-disability-and-critical-illness'] === 'Y' ||
      accel['total-personal-disability-and-critical-illness'] === 'Y'
    if (hasTpd) b.add('tpd')
    if (hasCi) b.add('ci')
    return b
  }
  if (opt === 'Standalone') {
    const standalone = state.form['standalone-options'] || {}
    if (standalone['death-sum-assured']) b.add('death')
    if (standalone['total-personal-disability-sum-assured']) b.add('tpd')
    if (standalone['illness-sum-assured']) b.add('ci')
    return b
  }
  return b
}

const ageLimitsForSelectedBenefits = (benefits) => {
  const effective =
    benefits && benefits.size > 0 ? benefits : new Set(['death'])
  let maxCease = Infinity
  effective.forEach((k) => {
    const lim = benefitAgeLimits[k]
    if (!lim) return
    maxCease = Math.min(maxCease, lim.maxCease)
  })
  return { maxCease }
}

const quoteMaxTermByAge = (state) => {
  const benefits = selectedBenefitsOnQuoteScreen(state)
  const limits = ageLimitsForSelectedBenefits(benefits)
  const mainDob = state.form['life-assured-identification']['date-of-birth']
  const mainAge = mainDob ? dayjs().diff(mainDob, 'year') : NaN
  if (!Number.isFinite(mainAge) || !Number.isFinite(limits.maxCease)) return NaN

  let maxTerm = Math.floor(limits.maxCease - mainAge)

  const includeSpouse =
    state.form['life-assured-identification']['marital-status'] === 'Married' &&
    state.form['joint-life'] === 'Y'
  const spouseDob = includeSpouse
    ? state.form['spouse-identification']['date-of-birth']
    : null
  if (includeSpouse && spouseDob) {
    const spouseAge = dayjs().diff(spouseDob, 'year')
    if (Number.isFinite(spouseAge)) {
      maxTerm = Math.min(maxTerm, Math.floor(limits.maxCease - spouseAge))
    }
  }

  return Number.isFinite(maxTerm) ? maxTerm : NaN
}

const rangeOptions = (min, max) => {
  const out = ['']
  for (let i = min; i <= max; i++) out.push(String(i))
  return out
}

const parentRelationships = new Set(['Mother', 'Father'])

const eligibleForOldAgeCover = (form, relationship) => {
  const r = String(relationship || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')

  if (r === 'mother' || r === 'father') return true
  if (form && form['joint-life'] === 'Y') {
    if (r === 'mother in law' || r === 'father in law') return true
  }
  return false
}

const parentDetailsRoleKeys = new Set([
  'mother',
  'father',
  'step mother',
  'step father',
])

const normalizedRelationshipKey = (relationship) =>
  String(relationship || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')

const isParentDetailsRole = (relationship) =>
  parentDetailsRoleKeys.has(normalizedRelationshipKey(relationship))

const syncParentDetailsFromAdditionalLives = (form) => {
  if (!form || typeof form !== 'object') return
  const lives = form['additional-lives']
  if (!Array.isArray(lives)) {
    form['parent-details'] = []
    return
  }
  form['parent-details'] = lives
    .filter((life) => life && isParentDetailsRole(life.relationship))
    .map((life) => ({
      'first-names': life['first-names'] != null ? life['first-names'] : null,
      surname: life.surname != null ? life.surname : null,
      'date-of-birth':
        life['date-of-birth'] != null ? life['date-of-birth'] : null,
      role: life.relationship != null ? life.relationship : null,
      gender: life.gender != null ? life.gender : null,
      'old-age-cover':
        life['old-age-cover'] != null ? life['old-age-cover'] : null,
      'old-age-cover-benefit':
        life['old-age-cover-benefit'] != null
          ? life['old-age-cover-benefit']
          : null,
    }))
}

const ensureExistingPolicyDetailsArray = (form) => {
  if (!form || typeof form !== 'object') return
  const cur = form['existing-policy-details']
  if (cur && !Array.isArray(cur) && typeof cur === 'object') {
    form['existing-policy-details'] = [0, 1, 2].map((i) => ({
      'company-name': cur[`company-name-${i + 1}`] || null,
      'existing-contract-id': cur[`existing-contract-id-${i + 1}`] || null,
      'sum-assured': cur[`sum-assured-${i + 1}`] || null,
    }))
    return
  }
  if (!Array.isArray(cur)) {
    form['existing-policy-details'] = []
  }
  while (form['existing-policy-details'].length < 3) {
    form['existing-policy-details'].push({
      'company-name': null,
      'existing-contract-id': null,
      'sum-assured': null,
    })
  }
}

const existingPolicyDetailsFieldValue = (form, index1, key) => {
  ensureExistingPolicyDetailsArray(form)
  const arr = form['existing-policy-details']
  const idx = index1 - 1
  const row =
    Array.isArray(arr) && idx >= 0 && idx < arr.length ? arr[idx] : null
  return row && row[key] != null ? row[key] : ''
}

const setExistingPolicyDetailsFieldValue = (form, index1, key, value) => {
  ensureExistingPolicyDetailsArray(form)
  const arr = form['existing-policy-details']
  const idx = index1 - 1
  if (!Array.isArray(arr) || idx < 0) return false
  while (arr.length <= idx) {
    arr.push({
      'company-name': null,
      'existing-contract-id': null,
      'sum-assured': null,
    })
  }
  if (!arr[idx] || typeof arr[idx] !== 'object') {
    arr[idx] = {
      'company-name': null,
      'existing-contract-id': null,
      'sum-assured': null,
    }
  }
  arr[idx][key] = value
  return true
}

const syncTpLivesAssuredFromAdditionalLives = (form) => {
  if (!form || typeof form !== 'object') return
  const lives = form['additional-lives']
  if (!Array.isArray(lives)) {
    form['tp-lives-assured'] = []
    return
  }
  form['tp-lives-assured'] = lives
    .filter((life) => life && life['transition-cover'] === 'Y')
    .map((life) => ({
      'first-names': life['first-names'] != null ? life['first-names'] : null,
      surname: life.surname != null ? life.surname : null,
      'date-of-birth':
        life['date-of-birth'] != null ? life['date-of-birth'] : null,
      role: life.relationship != null ? life.relationship : null,
      gender: life.gender != null ? life.gender : null,
    }))
}

//Pre set genders
const additionalLifeLockedGender = {
  Mother: 'Female',
  'Mother in law': 'Female',
  Father: 'Male',
  'Father in law': 'Male',
  Grandmother: 'Female',
  Grandfather: 'Male',
  Sister: 'Female',
  Brother: 'Male',
  'Step Mother': 'Female',
  'Step father': 'Male',
}

const mainLifeAge = (form) => {
  const id = form['life-assured-identification']
  if (!id || !id['date-of-birth']) {
    return 0
  }
  const d = dayjs(id['date-of-birth'], ['YYYYMMDD', 'YYYY-MM-DD'])
  if (!d.isValid()) {
    return 0
  }
  return dayjs().diff(d, 'year')
}

//Use spouse gender if available
const spouseAgeFromJointQuoteStr = (form) => {
  if (form['joint-life'] !== 'Y') {
    return null
  }
  const sid = form['spouse-identification']
  if (!sid || !sid['date-of-birth']) {
    return null
  }
  const d = dayjs(sid['date-of-birth'], ['YYYYMMDD', 'YYYY-MM-DD'])
  if (!d.isValid()) {
    return null
  }
  return `${dayjs().diff(d, 'year')}`
}

const spouseAdditionalLifeAgeState = (
  form,
  relationship,
  defaultAgeOptions
) => {
  if (relationship !== 'Spouse') {
    return {
      ageLocked: false,
      'age-of-member': '',
      ageOptions: defaultAgeOptions,
    }
  }
  const quoted = spouseAgeFromJointQuoteStr(form)
  if (quoted === null) {
    return {
      ageLocked: false,
      'age-of-member': '',
      ageOptions: defaultAgeOptions,
    }
  }
  return {
    ageLocked: true,
    'age-of-member': quoted,
    ageOptions: ['', quoted],
  }
}

const gotoEditAdditionalLife = (state, rowIndex) => {
  const lives = state.form['additional-lives'] || []
  const life = lives[rowIndex] && original(lives[rowIndex])
  if (!life) {
    return
  }
  const mlAge = mainLifeAge(state.form) || 0
  const tree = additionalLifeFamilyOptions(mlAge)
  let ageOpts = optionsForAdditionalLifeRelationship(tree, life.relationship)
  if (!ageOpts || !ageOpts.length) {
    ageOpts = [''].concat(range(0, 100))
  }
  const mainG = state.form['life-assured-identification'].gender
  const gg = applyAdditionalLifeGenderForRelationship(
    life.relationship,
    mainG,
    state.form['joint-life'] === 'Y'
  )
  const ageSt = spouseAdditionalLifeAgeState(
    state.form,
    life.relationship,
    ageOpts
  )
  const ageMember =
    life.relationship === 'Spouse' && ageSt.ageLocked
      ? ageSt['age-of-member']
      : life['age-of-member'] || ''
  changePage(state, 'family-members-age')
  state.local = Object.assign({}, life, {
    mode: 'edit',
    index: rowIndex,
    ageOptions: ageSt.ageOptions,
    'age-of-member': ageMember,
    ageLocked: ageSt.ageLocked,
    gender: gg.genderLocked ? gg.gender : life.gender || gg.gender,
    genderLocked: gg.genderLocked,
  })
}

const applyAdditionalLifeGenderForRelationship = (
  relationship,
  mainGender,
  jointLifeSelected
) => {
  if (relationship === 'Spouse') {
    // Set spouse gender to the opposite of main life gender.
    return {
      gender: spouseGender(mainGender) || '',
      genderLocked: true,
    }
  }
  if (additionalLifeLockedGender[relationship]) {
    return {
      gender: additionalLifeLockedGender[relationship],
      genderLocked: true,
    }
  }
  return { gender: '', genderLocked: false }
}

const syncAdditionalLivesGenderLocks = (state) => {
  const lives = original(state.form['additional-lives'] || [])
  if (!lives.length) return

  const mainG = state.form['life-assured-identification'].gender
  const jointLifeSelected = state.form['joint-life'] === 'Y'
  let dirty = false

  const next = lives.map((life) => {
    const gg = applyAdditionalLifeGenderForRelationship(
      life.relationship,
      mainG,
      jointLifeSelected
    )
    if (!gg.genderLocked) {
      return life
    }
    if (life.gender !== gg.gender) {
      dirty = true
      return Object.assign({}, life, { gender: gg.gender })
    }
    return life
  })

  if (dirty) {
    state.form['additional-lives'] = next
  }
}

const syncSpouseAdditionalLifeFromQuote = (state) => {
  if (state.form['joint-life'] !== 'Y') return
  const spouse = state.form['spouse-identification'] || {}
  if (
    !hasValue(spouse['first-names']) &&
    !hasValue(spouse.surname) &&
    !hasValue(spouse['date-of-birth'])
  ) {
    return
  }

  const lives = original(state.form['additional-lives'] || [])
  if (!lives.length) return

  let dirty = false
  const next = lives.map((life) => {
    if (!life || life.relationship !== 'Spouse') return life
    const updated = { ...life }
    ;[
      ['first-names', spouse['first-names']],
      ['surname', spouse.surname],
      ['date-of-birth', spouse['date-of-birth']],
    ].forEach(([k, v]) => {
      if (hasValue(v) && !hasValue(updated[k])) {
        updated[k] = v
        dirty = true
      }
    })
    return updated
  })

  if (dirty) {
    state.form['additional-lives'] = next
  }
}

const range = (start, end) =>
  new Array(end - start + 1).fill(undefined).map((_, i) => `${i + start}`)

const toNumber = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(`${v}`.replace(/,/g, ''))
  return Number.isFinite(n) ? n : 0
}

const mainLifeSumAssured = (form) => {
  if (form['benefit-option'] === 'Accelerated') {
    return toNumber(form['accelerated-options']['accelerated-sum-assured'])
  }
  const standalone = form['standalone-options'] || {}
  return (
    toNumber(standalone['death-sum-assured']) +
    toNumber(standalone['total-personal-disability-sum-assured']) +
    toNumber(standalone['illness-sum-assured'])
  )
}

//Spouse should be opposite to main life gender
const spouseGender = (gender) => {
  switch (gender) {
    case 'Male':
      return 'Female'
    case 'Female':
      return 'Male'
    default:
      return null
  }
}

const amountString = (f) =>
  f
    .toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    .replace(/,/, '')

const ageFromDob = (dob) => {
  if (!dob) return NaN
  const d = dayjs(dob, ['YYYYMMDD', 'YYYY-MM-DD'])
  return d.isValid() ? dayjs().diff(d, 'year') : NaN
}

const bandIndexForAge = (bands, age) => {
  if (!Array.isArray(bands) || !Number.isFinite(age)) return -1
  for (let i = 0; i < bands.length; i++) {
    const b = bands[i]
    if (Array.isArray(b) && age >= b[0] && age <= b[1]) return i
  }
  return -1
}

const toNumberOrZero = (v) => {
  const n =
    typeof v === 'number' ? v : parseFloat(String(v || '').replace(/,/g, ''))
  return Number.isFinite(n) ? n : 0
}

const selectedAcceleratedOptionNumber = (form) => {
  const a = (form && form['accelerated-options']) || {}
  if (a['death-and-total-disability-and-critical-illness'] === 'Y') return 4
  if (a['death-and-total-personal-disability'] === 'Y') return 5
  if (a['death-and-critical-illness'] === 'Y') return 6
  if (a['total-personal-disability-and-critical-illness'] === 'Y') return 7
  return null
}

const quoteRateContext = (form) => {
  const isJoint = form && form['joint-life'] === 'Y'
  const variant = form && form['joint-life-variant']
  if (!isJoint) return { kind: 'single', table: hnwRates.single }
  if (variant === 'Last') return { kind: 'joint', table: hnwRates.jointLast }
  return { kind: 'joint', table: hnwRates.jointFirst }
}

const quoteTermYears = (form) => {
  if (!form) return NaN
  if (form['benefit-option'] === 'Standalone') {
    return parseInt(
      form['standalone-options'] &&
        form['standalone-options']['standalone-term'],
      10
    )
  }
  if (form['benefit-option'] === 'Accelerated') {
    return parseInt(
      form['accelerated-options'] &&
        form['accelerated-options']['accelerated-term'],
      10
    )
  }
  return NaN
}

const hospitalisationPremiumFromForm = (form, term) => {
  const amount = toNumberOrZero(form && form['hospitalisation-units'])
  if (amount <= 0) return 0
  if (!Number.isFinite(term) || term <= 0) return 0

  const mainAge = ageFromDob(
    form &&
      form['life-assured-identification'] &&
      form['life-assured-identification']['date-of-birth']
  )
  const spouseAge = ageFromDob(
    form &&
      form['spouse-identification'] &&
      form['spouse-identification']['date-of-birth']
  )
  const mainGender =
    form &&
    form['life-assured-identification'] &&
    form['life-assured-identification'].gender

  const termKey = String(term)
  const isJoint = form && form['joint-life'] === 'Y'

  if (!isJoint) {
    const band = bandIndexForAge(
      hospitalisationRates.bands &&
        hospitalisationRates.bands.single &&
        hospitalisationRates.bands.single.age,
      mainAge
    )
    const termRow =
      hospitalisationRates.single && hospitalisationRates.single[termKey]
    const ratePerMille =
      band >= 0 && termRow && typeof termRow[band] === 'number'
        ? termRow[band]
        : 0
    return ratePerMille > 0 ? amount * (ratePerMille / 1000.0) : 0
  }

  const maleAge = mainGender === 'Female' ? spouseAge : mainAge
  const femaleAge = mainGender === 'Female' ? mainAge : spouseAge

  const maleBand = bandIndexForAge(
    hospitalisationRates.bands &&
      hospitalisationRates.bands.joint &&
      hospitalisationRates.bands.joint.male,
    maleAge
  )
  const femaleBand = bandIndexForAge(
    hospitalisationRates.bands &&
      hospitalisationRates.bands.joint &&
      hospitalisationRates.bands.joint.female,
    femaleAge
  )
  if (maleBand < 0 || femaleBand < 0) return 0

  const termTable =
    hospitalisationRates.joint && hospitalisationRates.joint[termKey]
  const maleRow = termTable && termTable[String(maleBand)]
  const ratePerMille =
    maleRow && typeof maleRow[femaleBand] === 'number' ? maleRow[femaleBand] : 0
  return ratePerMille > 0 ? amount * (ratePerMille / 1000.0) : 0
}

const transitionPlanAddOnsFromForm = (form) => {
  const coverAmount = toNumberOrZero(form && form['transition-plan'])
  if (coverAmount <= 0) return { sumAssuredAdd: 0, premiumAdd: 0 }

  const mainAge = ageFromDob(
    form &&
      form['life-assured-identification'] &&
      form['life-assured-identification']['date-of-birth']
  )
  const band = bandIndexForAge(
    transitionPlanRates &&
      transitionPlanRates.bands &&
      transitionPlanRates.bands.age,
    mainAge
  )

  const coverKey = String(parseInt(coverAmount, 10))
  const coverRow =
    transitionPlanRates &&
    transitionPlanRates.mainLife &&
    transitionPlanRates.mainLife[coverKey]
  const rate =
    band >= 0 && coverRow && typeof coverRow[band] === 'number'
      ? coverRow[band]
      : 0

  return {
    sumAssuredAdd: coverAmount,
    premiumAdd: rate > 0 ? rate : 0,
  }
}

const personalAccidentAddOnsFromForm = (form, term) => {
  const coverAmount = toNumberOrZero(form && form['personal-accident'])
  if (coverAmount <= 0) return { sumAssuredAdd: 0, premiumAdd: 0 }
  if (!Number.isFinite(term) || term <= 0)
    return { sumAssuredAdd: coverAmount, premiumAdd: 0 }

  const mainAge = ageFromDob(
    form &&
      form['life-assured-identification'] &&
      form['life-assured-identification']['date-of-birth']
  )
  const band = bandIndexForAge(
    personalAccidentRates &&
      personalAccidentRates.bands &&
      personalAccidentRates.bands.age,
    mainAge
  )

  const termKey = String(term)
  const termRow =
    personalAccidentRates &&
    personalAccidentRates.mainLife &&
    personalAccidentRates.mainLife[termKey]
  const ratePerMille =
    band >= 0 && termRow && typeof termRow[band] === 'number'
      ? termRow[band]
      : 0

  return {
    sumAssuredAdd: coverAmount,
    premiumAdd: ratePerMille > 0 ? coverAmount * (ratePerMille / 1000.0) : 0,
  }
}

const educationalSupportAddOnsFromForm = (form) => {
  const es = (form && form['educational-support-benefit']) || {}
  const amount = toNumberOrZero(es['educational-support-benefit'])
  const termYears = parseInt(es['educational-support-term'], 10)
  if (amount <= 0 || !Number.isFinite(termYears) || termYears <= 0) {
    return { sumAssuredAdd: 0, premiumAdd: 0 }
  }
  return {
    sumAssuredAdd: amount,
    premiumAdd: amount / 12.0 / termYears,
  }
}

const longTermSavingsAddOnsFromForm = (form) => {
  const lts = (form && form['long-term-savings']) || {}
  const premium = toNumberOrZero(lts['long-term-savings-premium'])
  return { premiumAdd: premium > 0 ? premium : 0 }
}

const funeralCoverAddOnsFromForm = (form) => {
  const f = (form && form['funeral-cover-options']) || {}
  const isTen = f['term-ten'] === 'Y'
  const isFifteen = f['term-fifteen'] === 'Y'
  const termKey = isFifteen ? 'term15' : isTen ? 'term10' : null
  if (!termKey) return { sumAssuredAdd: 0, premiumAdd: 0 }

  let coverAmount = 0
  Object.keys(f).forEach((k) => {
    if (k.startsWith('ghs-') && f[k] === 'Y') {
      const n = parseInt(k.replace('ghs-', ''), 10)
      if (Number.isFinite(n) && n > 0) coverAmount = n
    }
  })
  if (coverAmount <= 0) return { sumAssuredAdd: 0, premiumAdd: 0 }

  const mainAge = ageFromDob(
    form &&
      form['life-assured-identification'] &&
      form['life-assured-identification']['date-of-birth']
  )
  const band = bandIndexForAge(
    funeralCoverRates &&
      funeralCoverRates[termKey] &&
      funeralCoverRates[termKey].bands &&
      funeralCoverRates[termKey].bands.age,
    mainAge
  )

  const coverKey = String(coverAmount)
  const coverRow =
    funeralCoverRates &&
    funeralCoverRates[termKey] &&
    funeralCoverRates[termKey].deathBenefit &&
    funeralCoverRates[termKey].deathBenefit[coverKey]
  const rate =
    band >= 0 && coverRow && typeof coverRow[band] === 'number'
      ? coverRow[band]
      : 0

  return {
    sumAssuredAdd: coverAmount,
    premiumAdd: rate > 0 ? rate : 0,
  }
}

const funeralExtrasPremiumAddOnsFromForm = (form) => {
  const f = (form && form['funeral-cover-options']) || {}
  const isTen = f['term-ten'] === 'Y'
  const isFifteen = f['term-fifteen'] === 'Y'
  const termKey = isFifteen ? 'term15' : isTen ? 'term10' : null
  if (!termKey) return { premiumAdd: 0 }

  let coverAmount = 0
  Object.keys(f).forEach((k) => {
    if (k.startsWith('ghs-') && f[k] === 'Y') {
      const n = parseInt(k.replace('ghs-', ''), 10)
      if (Number.isFinite(n) && n > 0) coverAmount = n
    }
  })
  if (coverAmount <= 0) return { premiumAdd: 0 }

  const mainAge = ageFromDob(
    form &&
      form['life-assured-identification'] &&
      form['life-assured-identification']['date-of-birth']
  )
  const band = bandIndexForAge(
    funeralExtrasRates &&
      funeralExtrasRates[termKey] &&
      funeralExtrasRates[termKey].memorialOneYear &&
      funeralExtrasRates[termKey].memorialOneYear.bands &&
      funeralExtrasRates[termKey].memorialOneYear.bands.age,
    mainAge
  )
  if (band < 0) return { premiumAdd: 0 }

  const rateFor = (tableKey) => {
    const table =
      funeralExtrasRates &&
      funeralExtrasRates[termKey] &&
      funeralExtrasRates[termKey][tableKey]
    const row = table && table.rates && table.rates[String(coverAmount)]
    const v = row && typeof row[band] === 'number' ? row[band] : 0
    return v > 0 ? v : 0
  }

  const rider = (form && form['rider-options']) || {}
  let premiumAdd = 0
  if (rider['memorial-seven-day'] === 'Y')
    premiumAdd += rateFor('memorialSevenDay')
  if (rider['memorial-forty-day'] === 'Y')
    premiumAdd += rateFor('memorialFortyDay')
  if (rider['memorial-one-year'] === 'Y')
    premiumAdd += rateFor('memorialOneYear')

  if ((form && form['double-accident-benefit']) === 'Y') {
    premiumAdd += rateFor('doubleAccidental')
  }

  return { premiumAdd }
}

const additionalLivesFuneralAddOnsFromForm = (form) => {
  const f = (form && form['funeral-cover-options']) || {}
  const isTen = f['term-ten'] === 'Y'
  const isFifteen = f['term-fifteen'] === 'Y'
  const mainTermKey = isFifteen ? 'term15' : isTen ? 'term10' : null

  const lives = (form && form['additional-lives']) || []
  if (!Array.isArray(lives) || lives.length < 1)
    return { sumAssuredAdd: 0, premiumAdd: 0 }

  let sumAssuredAdd = 0
  let premiumAdd = 0

  const termKeyForLife = (life) => {
    // If main life selected funeral cover, additional lives share that term.
    if (form && form['funeral-cover-for-main-life'] === 'Y') {
      return mainTermKey
    }
    // Otherwise, additional life must choose own funeral term.
    const v = life && life['funeral-cover-term']
    if (v === '15') return 'term15'
    if (v === '10') return 'term10'
    return null
  }

  lives.forEach((life) => {
    if (!life || life['funeral-cover'] !== 'Y') return
    const termKey = termKeyForLife(life)
    if (!termKey) return

    const coverAmount = toNumberOrZero(life['funeral-cover-benefit'])
    if (coverAmount <= 0) return

    const age = parseInt(life['age-of-member'], 10)
    if (!Number.isFinite(age)) return

    const deathTable =
      funeralCoverRates &&
      funeralCoverRates[termKey] &&
      funeralCoverRates[termKey].deathBenefit
    const deathBands =
      funeralCoverRates &&
      funeralCoverRates[termKey] &&
      funeralCoverRates[termKey].bands &&
      funeralCoverRates[termKey].bands.age
    const band = bandIndexForAge(deathBands, age)
    if (band < 0) return

    const coverRow = deathTable && deathTable[String(parseInt(coverAmount, 10))]
    const rate =
      coverRow && typeof coverRow[band] === 'number' ? coverRow[band] : 0

    sumAssuredAdd += coverAmount
    if (rate > 0) premiumAdd += rate

    const extras = funeralExtrasRates && funeralExtrasRates[termKey]
    const extrasBands =
      extras &&
      extras.memorialOneYear &&
      extras.memorialOneYear.bands &&
      extras.memorialOneYear.bands.age
    const extrasBand = bandIndexForAge(extrasBands, age)
    if (extrasBand < 0) return
    const rateForExtras = (tableKey) => {
      const table = extras && extras[tableKey]
      const row =
        table && table.rates && table.rates[String(parseInt(coverAmount, 10))]
      const v = row && typeof row[extrasBand] === 'number' ? row[extrasBand] : 0
      return v > 0 ? v : 0
    }
    if (life['memorial-seven-day'] === 'Y')
      premiumAdd += rateForExtras('memorialSevenDay')
    if (life['memorial-forty-day'] === 'Y')
      premiumAdd += rateForExtras('memorialFortyDay')
    if (life['memorial-one-year'] === 'Y')
      premiumAdd += rateForExtras('memorialOneYear')
  })

  return { sumAssuredAdd, premiumAdd }
}

const additionalLivesTransitionAddOnsFromForm = (form) => {
  const coverAmount = toNumberOrZero(form && form['transition-plan'])
  if (coverAmount <= 0) return { sumAssuredAdd: 0, premiumAdd: 0 }

  const lives = (form && form['additional-lives']) || []
  if (!Array.isArray(lives) || lives.length < 1)
    return { sumAssuredAdd: 0, premiumAdd: 0 }

  const relationshipKeyForLife = (relationship) => {
    const r = String(relationship || '')
      .trim()
      .toLowerCase()
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')

    if (r === 'spouse') return 'spouse'
    if (r === 'child' || r === 'children') return 'children'

    if (
      r === 'sibling' ||
      r === 'siblings' ||
      r === 'brother' ||
      r === 'sister'
    )
      return 'sibling'

    //Wider
    if (
      r === 'guardian' ||
      r === 'mother' ||
      r === 'father' ||
      r === 'parent' ||
      r === 'parents' ||
      r === 'parent in law' ||
      r === 'parent in laws' ||
      r === 'parent in laws,' ||
      r === 'step parent' ||
      r === 'step parents' ||
      r === 'step mother' ||
      r === 'step father' ||
      r === 'mother in law' ||
      r === 'father in law'
    )
      return 'parentsGuardians'

    if (
      r === 'grandmother' ||
      r === 'grandfather' ||
      r === 'grandparent' ||
      r === 'grandparents'
    )
      return 'grandparents'

    return null
  }

  let sumAssuredAdd = 0
  let premiumAdd = 0

  lives.forEach((life) => {
    if (!life || life['transition-cover'] !== 'Y') return
    const relKey = relationshipKeyForLife(life.relationship)
    if (!relKey) return

    const table =
      additionalLivesTransitionRates && additionalLivesTransitionRates[relKey]
    const bands = table && table.bands && table.bands.age
    const band = bandIndexForAge(bands, parseInt(life['age-of-member'], 10))
    if (band < 0) return

    const coverRow =
      table && table.rates && table.rates[String(parseInt(coverAmount, 10))]
    const rate =
      coverRow && typeof coverRow[band] === 'number' ? coverRow[band] : 0

    sumAssuredAdd += coverAmount
    if (rate > 0) premiumAdd += rate
  })

  return { sumAssuredAdd, premiumAdd }
}

const additionalLivesOldAgeAddOnsFromForm = (form, term) => {
  if (!Number.isFinite(term) || term <= 0) return { premiumAdd: 0 }
  const lives = (form && form['additional-lives']) || []
  if (!Array.isArray(lives) || lives.length < 1) return { premiumAdd: 0 }

  const termKey = String(term)
  const termRow =
    oldAgeRates && oldAgeRates.mainLife && oldAgeRates.mainLife[termKey]
  const bands = oldAgeRates && oldAgeRates.bands && oldAgeRates.bands.age

  let premiumAdd = 0

  lives.forEach((life) => {
    if (!life || life['old-age-cover'] !== 'Y') return
    if (!eligibleForOldAgeCover(form, life.relationship)) return

    const amount = toNumberOrZero(life['old-age-cover-benefit'])
    if (amount <= 0) return

    const age = parseInt(life['age-of-member'], 10)
    const band = bandIndexForAge(bands, age)
    if (band < 0) return

    const ratePerMille =
      termRow && typeof termRow[band] === 'number' ? termRow[band] : 0
    if (ratePerMille > 0) {
      premiumAdd += amount * (ratePerMille / 1000.0)
    }
  })

  return { premiumAdd }
}

const quoteMonthlyRateAndPremium = (form) => {
  const term = quoteTermYears(form)
  const hospitalPremium = hospitalisationPremiumFromForm(form, term)
  const transitionAddOns = transitionPlanAddOnsFromForm(form)
  const personalAccidentAddOns = personalAccidentAddOnsFromForm(form, term)
  const educationalSupportAddOns = educationalSupportAddOnsFromForm(form)
  const longTermSavingsAddOns = longTermSavingsAddOnsFromForm(form)
  const funeralCoverAddOns = funeralCoverAddOnsFromForm(form)
  const funeralExtrasAddOns = funeralExtrasPremiumAddOnsFromForm(form)
  const additionalLivesFuneralAddOns =
    additionalLivesFuneralAddOnsFromForm(form)
  const additionalLivesTransitionAddOns =
    additionalLivesTransitionAddOnsFromForm(form)
  const additionalLivesOldAgeAddOns = additionalLivesOldAgeAddOnsFromForm(
    form,
    term
  )
  const withHospitalPremium = (result) => ({
    ...result,
    sumAssured:
      (result.sumAssured || 0) +
      (transitionAddOns.sumAssuredAdd || 0) +
      (personalAccidentAddOns.sumAssuredAdd || 0) +
      (educationalSupportAddOns.sumAssuredAdd || 0) +
      (funeralCoverAddOns.sumAssuredAdd || 0) +
      (additionalLivesFuneralAddOns.sumAssuredAdd || 0) +
      (additionalLivesTransitionAddOns.sumAssuredAdd || 0),
    premium:
      (result.premium || 0) +
      hospitalPremium +
      (transitionAddOns.premiumAdd || 0) +
      (personalAccidentAddOns.premiumAdd || 0) +
      (educationalSupportAddOns.premiumAdd || 0) +
      (longTermSavingsAddOns.premiumAdd || 0) +
      (funeralCoverAddOns.premiumAdd || 0) +
      (funeralExtrasAddOns.premiumAdd || 0) +
      (additionalLivesFuneralAddOns.premiumAdd || 0) +
      (additionalLivesTransitionAddOns.premiumAdd || 0) +
      (additionalLivesOldAgeAddOns.premiumAdd || 0),
  })

  if (!Number.isFinite(term) || term <= 0)
    return { sumAssured: 0, ratePerMille: 0, premium: 0 }

  const mainAge = ageFromDob(
    form &&
      form['life-assured-identification'] &&
      form['life-assured-identification']['date-of-birth']
  )
  const spouseAge = ageFromDob(
    form &&
      form['spouse-identification'] &&
      form['spouse-identification']['date-of-birth']
  )
  const mainGender =
    form &&
    form['life-assured-identification'] &&
    form['life-assured-identification'].gender
  const spouseGenderValue = spouseGender(mainGender)

  const ctx = quoteRateContext(form)
  const opt = form && form['benefit-option']

  const lookupSingle = (optionNumber, ageBandIndex) => {
    const optTable = ctx.table && ctx.table[String(optionNumber)]
    const termRow = optTable && optTable[String(term)]
    const v = termRow && termRow[ageBandIndex]
    return typeof v === 'number' ? v : 0
  }

  const lookupJoint = (optionNumber, maleBandIndex, femaleBandIndex) => {
    const optTable = ctx.table && ctx.table[String(optionNumber)]
    const termRow = optTable && optTable[String(term)]
    const maleRow = termRow && termRow[String(maleBandIndex)]
    const v = maleRow && maleRow[femaleBandIndex]
    return typeof v === 'number' ? v : 0
  }

  const bandSingle = bandIndexForAge(
    hnwRates.bands && hnwRates.bands.single && hnwRates.bands.single.age,
    mainAge
  )
  const maleBand = bandIndexForAge(
    hnwRates.bands && hnwRates.bands.joint && hnwRates.bands.joint.male,
    mainGender === 'Male' ? mainAge : spouseAge
  )
  const femaleBand = bandIndexForAge(
    hnwRates.bands && hnwRates.bands.joint && hnwRates.bands.joint.female,
    mainGender === 'Female' ? mainAge : spouseAge
  )

  const rateForOption = (optionNumber) => {
    if (ctx.kind === 'single') {
      return bandSingle >= 0 ? lookupSingle(optionNumber, bandSingle) : 0
    }
    if (maleBand < 0 || femaleBand < 0) return 0
    return lookupJoint(optionNumber, maleBand, femaleBand)
  }

  if (opt === 'Accelerated') {
    const optionNumber = selectedAcceleratedOptionNumber(form)
    const sumAssured = toNumberOrZero(
      form['accelerated-options'] &&
        form['accelerated-options']['accelerated-sum-assured']
    )
    if (!optionNumber || sumAssured <= 0)
      return withHospitalPremium({ sumAssured: 0, ratePerMille: 0, premium: 0 })
    const ratePerMille = rateForOption(optionNumber)
    let premium = ratePerMille * (sumAssured / 1000.0)
    if (premium > 0) premium += 1.0
    return withHospitalPremium({ sumAssured, ratePerMille, premium })
  }

  if (opt === 'Standalone') {
    const standalone = form['standalone-options'] || {}
    const parts = [
      { opt: 1, sa: toNumberOrZero(standalone['death-sum-assured']) },
      {
        opt: 2,
        sa: toNumberOrZero(standalone['total-personal-disability-sum-assured']),
      },
      { opt: 3, sa: toNumberOrZero(standalone['illness-sum-assured']) },
    ].filter((p) => p.sa > 0)
    const totalSA = parts.reduce((s, p) => s + p.sa, 0)
    if (totalSA <= 0)
      return withHospitalPremium({ sumAssured: 0, ratePerMille: 0, premium: 0 })
    let premium = parts.reduce(
      (s, p) => s + rateForOption(p.opt) * (p.sa / 1000.0),
      0
    )
    if (premium > 0) premium += 1.0
    const ratePerMille = (premium * 1000.0) / totalSA
    return withHospitalPremium({ sumAssured: totalSA, ratePerMille, premium })
  }

  return withHospitalPremium({ sumAssured: 0, ratePerMille: 0, premium: 0 })
}

const isForcesPayOffice = (state) =>
  state.form['payment-method-selection'] &&
  state.form['payment-method-selection']['stop-order'] === 'Y' &&
  state.form['payment-employment-details'] &&
  state.form['payment-employment-details']['employer-organisation'] ===
    'Forces Pay Office'

const ensureForcesPayOfficeDetails = (state) => {
  if (!state.form['forces-pay-office-details']) {
    state.form['forces-pay-office-details'] = Object.assign(
      {},
      form['forces-pay-office-details']
    )
  }
}

const formattedPremiumForFpo = (form) =>
  amountString(frequencyAdjustedPremium(form) || 0)

const paymentFrequencyLookup = {
  monthly: 1,
  quarterly: 3,
  'bi-annual': 6,
  annual: 12,
}

const selectedPaymentFrequencyKey = (form) => {
  const pf = (form && form['payment-frequency']) || {}
  const key = Object.keys(pf).find((k) => pf[k] === 'Y')
  return key || 'monthly'
}

const frequencyAdjustedPremium = (form) => {
  const qp = quoteMonthlyRateAndPremium(form)
  const monthlyTotal = (qp && qp.premium) || 0
  const lts = (form && form['long-term-savings']) || {}
  const ltsMonthly = toNumberOrZero(lts['long-term-savings-premium'])
  const riskMonthly = Math.max(0, monthlyTotal - ltsMonthly)

  const pfKey = selectedPaymentFrequencyKey(form)
  const baseMult =
    pfKey in paymentFrequencyLookup ? paymentFrequencyLookup[pfKey] : 1
  const riskMult = pfKey === 'annual' ? baseMult * 0.9 : baseMult
  const ltsMult = baseMult // no annual discount for long term savings

  return riskMonthly * riskMult + ltsMonthly * ltsMult
}

const startOfNextMonths = (monthCount) => {
  const result = []
  const now = dayjs()
  for (let m = 1; m <= monthCount; m++) {
    result.push(now.add(m, 'month').startOf('month').format(dateFormat))
  }
  return result
}

//Is Trustee The Beneficiary
const isTrusteeABeneficiary = createSelector(
  (form) => form['trustee-identification']['first-names'],
  (form) => form['trustee-identification'].surname,
  (form) => form['trustee-identification']['date-of-birth'],
  (form) => form['beneficiary-details'],
  (fn, sn, dob, bs) =>
    fn &&
    sn &&
    dob &&
    bs.reduce(
      (result, b) =>
        result ||
        (b['first-names'] === fn &&
          b.surname === sn &&
          b['date-of-birth'] === dob),
      false
    )
)

const isAddedLivesBeneficiary = (life, bs) =>
  life['first-names'] &&
  life.surname &&
  life['date-of-birth'] &&
  bs.reduce(
    (result, b) =>
      result ||
      (b['first-names'] === life['first-names'] &&
        b.surname === life.surname &&
        b['date-of-birth'] === life['date-of-birth']),
    false
  )

const updateBeneficiarySplit = createSelector(
  (form) => form['beneficiary-details'],
  (beneficiaries) => {
    if (beneficiaries.length > 0) {
      const split = Math.floor(100 / beneficiaries.length)
      let difference = 100 - split * beneficiaries.length
      for (let i = 0; i < beneficiaries.length; i++) {
        if (difference > 0) {
          beneficiaries[i]['benefit-split'] = amountString(split + 1)
          difference -= 1
        } else {
          beneficiaries[i]['benefit-split'] = amountString(split)
        }
      }
    }
    return beneficiaries
  }
)

const healthConcernCount = (form) =>
  Object.keys(form['life-assured-health-details']).reduce(
    (t, f) =>
      t +
      (form['life-assured-health-details'][f] === 'Y' ||
      form['life-assured-health-details'][f] === 'Yes'
        ? 1
        : 0),
    0
  )

const createHealthDetailsPage = (state) => {
  const maxConcerns = Math.min(4, healthConcernCount(original(state.form)))
  updateItemIn(
    state.page.item,
    { id: 'health-descriptions', type: 'list' },
    {
      content: healthDetailsPrefix
        .concat(healthDetailsItem(maxConcerns))
        .concat([quotationPanel]),
    }
  )
  if (state.form['health-concerns'].length !== maxConcerns) {
    while (state.form['health-concerns'].length > maxConcerns) {
      state.form['health-concerns'].pop()
    }
    for (let i = state.form['health-concerns'].length; i < maxConcerns; i++) {
      state.form['health-concerns'].push({
        complaints: null,
        'complaint-description': null,
        'medical-condition-date': null,
        'health-results': null,
      })
    }
  }
}

const changePage = (state, page) => {
  if (page in pages) {
    state.page = pages[page]
    state.step = page
    state.local = null
  }
}

const generateAcceptancePage = (state) => {
  //Update the reference number
  updateItemIn(
    state.page.item,
    { id: 'contract-id' },
    { content: state.form['reference-number'] }
  )
  // Update all sections
  Object.keys(state.form).forEach((section) => {
    findItemsIn(state.page.item, { id: section }).forEach((sItem) => {
      Object.keys(state.form[section]).forEach((field) => {
        if (state.form[section][field]) {
          updateItemIn(
            sItem,
            { id: field },
            { content: state.form[section][field] }
          )
        }
      })
    })
  })
  // Add beneficiary details
  updateItemIn(
    state.page.item,
    { id: 'beneficiary-details', type: 'table' },
    { content: (state.form['beneficiary-details'] || []).map(beneficiaryItem) }
  )

  const additionalLives = state.form['additional-lives'] || []
  updateItemIn(
    state.page.item,
    { id: 'other-lives-assured-funeral-cover', type: 'table' },
    {
      invisible: additionalLives.length < 1,
      content: additionalLives.map((life, idx, arr) =>
        otherLivesAcceptanceItem(life, idx, arr)
      ),
    }
  )

  // Update the payment details
  let paymentDetails = acceptancePaymentDetails(state.form)
  if (paymentDetails) {
    updateItemIn(
      state.page.item,
      { id: 'payment-details', type: 'list' },
      paymentDetails
    )
  }
}

const syncQuotationPanelValues = (state) => {
  const qpAll = quoteMonthlyRateAndPremium(state.form)
  updateItemIn(
    state.page.item,
    { id: 'benefit-rate' },
    {
      content: amountString(qpAll.sumAssured || 0),
    }
  )
  updateItemIn(
    state.page.item,
    { id: 'premium' },
    {
      content: amountString(qpAll.premium || 0),
    }
  )
}

const setPageItemFromState = (state) => {
  validate(state, state.local && state.local.failed_validation ? false : true)
  syncParentDetailsFromAdditionalLives(state.form)
  syncTpLivesAssuredFromAdditionalLives(state.form)
  ensureExistingPolicyDetailsArray(state.form)
  syncQuotationPanelValues(state)
  switch (state.step) {
    case 'quotation-details':
      // Date of Birth
      updateItemIn(
        state.page.item,
        { id: 'date-of-birth', uid: 'f0e0a8a867c4bd0c' },
        {
          content:
            state.form['life-assured-identification']['date-of-birth'] || '',
        }
      )

      updateItemIn(
        state.page.item,
        { id: 'marital-status' },
        {
          content:
            state.form['life-assured-identification']['marital-status'] || '',
        }
      )
      updateItemIn(
        state.page.item,
        { id: 'gender', type: 'field' },
        { content: state.form['life-assured-identification']['gender'] || '' }
      )

      // Joint life (only visible for Married - see below)
      updateItemIn(
        state.page.item,
        { id: 'joint-life' },
        {
          content: state.form['joint-life'] || '',
        }
      )
      updateItemIn(
        state.page.item,
        { id: 'main-life-only' },
        {
          content: state.form['main-life-only'] || '',
        }
      )

      // Spouse details (quote screen)
      updateItemIn(
        state.page.item,
        { id: 'date-of-birth', uid: '0d8e0852303cf721' },
        { content: state.form['spouse-identification']['date-of-birth'] || '' }
      )
      updateItemIn(
        state.page.item,
        { id: 'joint-life-variant' },
        {
          content: state.form['joint-life-variant'] || '',
        }
      )

      //Select the Benefit Option
      updateItemIn(
        state.page.item,
        { id: 'benefit-option' },
        { content: state.form['benefit-option'] || '' }
      )
      //Standalone
      Object.keys(state.form['standalone-options']).forEach((field) =>
        updateItemIn(
          state.page.item,
          { id: field },
          { content: state.form['standalone-options'][field] || '' }
        )
      )

      //Accelerated
      Object.keys(state.form['accelerated-options']).forEach((field) => {
        updateItemIn(
          state.page.item,
          { id: field },
          { content: state.form['accelerated-options'][field] || '' }
        )
      })

      // Term dropdown options depend on age + selected benefits cease ages.
      const maxTermByAge = quoteMaxTermByAge(state)
      const termMax =
        Number.isFinite(maxTermByAge) && maxTermByAge >= 5 ? maxTermByAge : 15
      updateItemIn(
        state.page.item,
        { id: 'standalone-term' },
        {
          options: rangeOptions(5, termMax),
        }
      )
      updateItemIn(
        state.page.item,
        { id: 'accelerated-term' },
        {
          options: rangeOptions(5, termMax),
        }
      )

      const qp = quoteMonthlyRateAndPremium(state.form)
      updateItemIn(
        state.page.item,
        { id: 'benefit-rate' },
        {
          content: amountString(qp.sumAssured || 0),
        }
      )
      updateItemIn(
        state.page.item,
        { id: 'premium' },
        {
          content: amountString(qp.premium || 0),
        }
      )

      //Update Premium Option
      updateItemIn(
        state.page.item,
        { id: 'update-option', type: 'field' },
        { content: state.form['premium-updates'] || '' }
      )

      // Main life funeral cover (quote screen)
      updateItemIn(
        state.page.item,
        { id: 'funeral-cover-for-main-life' },
        {
          content: state.form['funeral-cover-for-main-life'] || 'N',
        }
      )

      // Hide elements when a benefit option has not been chosen yet.
      updateItemIn(
        state.page.item,
        { id: 'standalone-options', type: 'list' },
        {
          invisible: state.form['benefit-option'] !== 'Standalone',
        }
      )
      updateItemIn(
        state.page.item,
        { id: 'accelerated-options', type: 'list' },
        {
          invisible: state.form['benefit-option'] !== 'Accelerated',
        }
      )

      updateItemIn(
        state.page.item,
        { id: 'joint-life-options', type: 'list' },
        {
          invisible:
            state.form['life-assured-identification']['marital-status'] !==
            'Married',
        }
      )

      updateItemIn(
        state.page.item,
        { id: 'spouse-details', type: 'list' },
        {
          invisible: !(
            state.form['life-assured-identification']['marital-status'] ===
              'Married' && state.form['joint-life'] === 'Y'
          ),
        }
      )
      break
    case 'additional-benefits':
      const qpAdd = quoteMonthlyRateAndPremium(state.form)
      updateItemIn(
        state.page.item,
        { id: 'benefit-rate' },
        {
          content: amountString(qpAdd.sumAssured || 0),
        }
      )
      updateItemIn(
        state.page.item,
        { id: 'premium' },
        {
          content: amountString(qpAdd.premium || 0),
        }
      )

      updateItemIn(
        state.page.item,
        { id: 'hospitalisation-units', type: 'amount' },
        { content: state.form['hospitalisation-units'] || '' }
      )

      updateItemIn(
        state.page.item,
        { id: 'transition-plan', type: 'amount' },
        { content: state.form['transition-plan'] || '' }
      )

      updateItemIn(
        state.page.item,
        { id: 'personal-accident', type: 'amount' },
        { content: state.form['personal-accident'] || '' }
      )
      updateItemIn(
        state.page.item,
        { id: 'educational-support-benefit', type: 'real' },
        {
          content:
            state.form['educational-support-benefit'][
              'educational-support-benefit'
            ] || '',
        }
      )
      updateItemIn(
        state.page.item,
        { id: 'educational-support-term', type: 'integer' },
        {
          content:
            state.form['educational-support-benefit'][
              'educational-support-term'
            ] || '',
        }
      )
      ;['long-term-savings-premium', 'savings-term'].forEach((field) =>
        updateItemIn(
          state.page.item,
          { id: field },
          { content: state.form['long-term-savings'][field] || '' }
        )
      )

      // Funeral cover options only available if selected on Quote screen
      updateItemIn(
        state.page.item,
        { id: 'funeral-cover-options', type: 'list' },
        { invisible: state.form['funeral-cover-for-main-life'] !== 'Y' }
      )

      Object.keys(state.form['funeral-cover-options']).forEach((field) =>
        updateItemIn(
          state.page.item,
          { id: field },
          {
            content: state.form['funeral-cover-options'][field] || '',
          }
        )
      )

      Object.keys(state.form['rider-options']).forEach((field) =>
        updateItemIn(
          state.page.item,
          { id: field },
          {
            content: state.form['rider-options'][field] || '',
          }
        )
      )

      updateItemIn(
        state.page.item,
        { id: 'double-accident-benefit' },
        {
          content: state.form['double-accident-benefit'] || '',
        }
      )
      break

    case 'quotation-family': {
      const lives = state.form['additional-lives'] || []
      updateItemIn(
        state.page.item,
        { id: 'all-lives-added', type: 'table' },
        {
          content: lives.map((life, idx, arr) =>
            otherLivesItem(life, idx, arr)
          ),
        }
      )
      state.page.buttons.forEach((b) => {
        if (b.id === 'remove-family' || b.id === 'edit-family') {
          b.invisible = lives.length < 1
        }
      })
      break
    }

    case 'remove-family-members': {
      const livesRm = state.form['additional-lives'] || []
      state.page.buttons = livesRm.map(buttonFromAdditionalLife)
      state.page.buttons.push(createButton('Back', { control: true }))
      break
    }

    case 'edit-family-members': {
      const livesEd = state.form['additional-lives'] || []
      state.page.buttons = livesEd.map(buttonFromAdditionalLife)
      state.page.buttons.push(createButton('Back', { control: true }))
      break
    }

    case 'add-family-members': {
      const mlAge = mainLifeAge(state.form) || 0
      let familyButtonLabels = additionalLifeFamilyLabels
      const loc = state.local || {}
      if (
        loc.relationshipCategory &&
        additionalLifeFamilyLabels.indexOf(loc.relationshipCategory) >= 0
      ) {
        const sub =
          additionalLifeFamilyOptions(mlAge)[loc.relationshipCategory] || {}
        familyButtonLabels = Object.keys(sub)
      } else {
        const hasSpouse = (state.form['additional-lives'] || []).some(
          (l) => l.relationship === 'Spouse'
        )
        if (hasSpouse) {
          familyButtonLabels = familyButtonLabels.filter((b) => b !== 'Spouse')
        }
      }
      state.page.buttons = familyButtonLabels.map((label) =>
        createButton(label)
      )
      state.page.buttons.push(createButton('Back', { control: true }))
      break
    }

    case 'family-members-age': {
      const loc = state.local || {}
      const ages =
        loc.ageOptions && loc.ageOptions.length
          ? loc.ageOptions
          : [''].concat(range(0, 100))
      updateItemIn(
        state.page.item,
        { id: 'age-of-member', type: 'integer' },
        {
          content: loc['age-of-member'] || '',
          elementType: loc.ageLocked ? 'input' : 'select',
          inputType: loc.ageLocked ? 'text' : undefined,
          options: loc.ageLocked ? undefined : ages,
          readonly: !!loc.ageLocked,
        }
      )
      updateItemIn(
        state.page.item,
        { id: 'gender', type: 'field' },
        {
          content: loc.gender || '',
          // When gender is locked, render as read-only
          elementType: loc.genderLocked ? 'input' : 'select',
          inputType: 'text',
          options: loc.genderLocked ? undefined : ['', 'Female', 'Male'],
          readonly: !!loc.genderLocked,
        }
      )
      updateItemIn(
        state.page.item,
        { id: 'funeral-cover', type: 'field' },
        {
          content: loc['funeral-cover'] || '',
        }
      )
      updateItemIn(
        state.page.item,
        { id: 'additional-life-transition-block', type: 'list' },
        {
          invisible: !state.form['transition-plan'],
        }
      )
      updateItemIn(
        state.page.item,
        { id: 'transition-cover', type: 'field' },
        {
          content: loc['transition-cover'] || 'N',
        }
      )
      updateItemIn(
        state.page.item,
        { id: 'additional-life-old-age-block', type: 'list' },
        {
          invisible: !eligibleForOldAgeCover(state.form, loc.relationship),
        }
      )
      updateItemIn(
        state.page.item,
        { id: 'old-age-cover', type: 'field' },
        {
          content: loc['old-age-cover'] || '',
        }
      )

      const livesForBanner = state.form['additional-lives'] || []
      const editingBanner =
        loc.mode === 'edit' &&
        typeof loc.index === 'number' &&
        livesForBanner[loc.index] != null

      state.page.buttons = pages['family-members-age'].buttons.map((b) =>
        Object.assign({}, b)
      )
      if (editingBanner) {
        state.page.buttons.push(
          createButton('hide', {
            id: 'delete-this-additional-life',
            invisible: true,
            control: false,
          })
        )
      }
      updateItemIn(
        state.page.item,
        {
          id: 'additional-life-edit-banner',
          type: 'html',
        },
        {
          invisible: !editingBanner,
          content: editingBanner
            ? `<p>You are editing <strong>${additionalLifeDisplayRelationship(
                livesForBanner[loc.index],
                loc.index,
                livesForBanner
              )}</strong>. <a href="#delete-this-additional-life">Remove this life</a>.</p>\n`
            : '',
        }
      )

      break
    }

    case 'family-members-quotation-details': {
      const loc = state.local || {}
      updateItemIn(
        state.page.item,
        { id: 'funeral-cover-benefit', type: 'amount' },
        {
          content: loc['funeral-cover-benefit'] || '',
        }
      )
      updateItemIn(
        state.page.item,
        { id: 'additional-life-funeral-term', type: 'list' },
        {
          invisible:
            loc['funeral-cover'] !== 'Y' ||
            state.form['funeral-cover-for-main-life'] === 'Y',
        }
      )
      updateItemIn(
        state.page.item,
        { id: 'additional-life-term-ten', type: 'field' },
        {
          content: loc['funeral-cover-term'] === '10' ? 'Y' : 'N',
        }
      )
      updateItemIn(
        state.page.item,
        { id: 'additional-life-term-fifteen', type: 'field' },
        {
          content: loc['funeral-cover-term'] === '15' ? 'Y' : 'N',
        }
      )
      ;[
        'memorial-seven-day',
        'memorial-forty-day',
        'memorial-one-year',
      ].forEach((id) =>
        updateItemIn(
          state.page.item,
          { id, type: 'field' },
          {
            content: loc[id] || '',
          }
        )
      )
      updateItemIn(
        state.page.item,
        { id: 'old-age-cover-benefit', type: 'real' },
        {
          content: loc['old-age-cover-benefit'] || '',
        }
      )

      updateItemIn(
        state.page.item,
        { id: 'funeral-benefit', type: 'list' },
        {
          invisible: loc['funeral-cover'] !== 'Y',
        }
      )
      updateItemIn(
        state.page.item,
        { id: 'transition-cover', type: 'list' },
        {
          invisible: loc['transition-cover'] !== 'Y',
        }
      )
      updateItemIn(
        state.page.item,
        { id: 'old-age-cover', type: 'list' },
        {
          invisible: loc['old-age-cover'] !== 'Y',
        }
      )

      const livesQz = state.form['additional-lives'] || []
      const editingQz =
        loc.mode === 'edit' &&
        typeof loc.index === 'number' &&
        livesQz[loc.index] != null

      state.page.buttons = pages[
        'family-members-quotation-details'
      ].buttons.map((b) => Object.assign({}, b))
      if (editingQz) {
        state.page.buttons.push(
          createButton('hide', {
            id: 'delete-this-additional-life',
            invisible: true,
            control: false,
          })
        )
      }
      updateItemIn(
        state.page.item,
        {
          id: 'additional-life-edit-banner',
          type: 'html',
        },
        {
          invisible: !editingQz,
          content: editingQz
            ? `<p>You are editing <strong>${additionalLifeDisplayRelationship(
                livesQz[loc.index],
                loc.index,
                livesQz
              )}</strong>. <a href="#delete-this-additional-life">Remove this life</a>.</p>\n`
            : '',
        }
      )

      break
    }

    case 'personal-details':
      ;[
        'life-assured-identification',
        'life-assured-contact-details',
        'life-assured-postal-address',
        'life-assured-residential-address',
        'life-assured-digital-address',
        'life-assured-employment-details',
      ].forEach((section) =>
        Object.keys(state.form[section]).forEach((name) =>
          updateItemIn(
            state.page.item,
            { id: name },
            { content: state.form[section][name] || '' }
          )
        )
      )
      updateItemIn(
        state.page.item,
        { id: 'other-nationality' },
        {
          invisible:
            state.form['life-assured-identification']['ghanaian'] === 'Y',
        }
      )
      if (state.local && state.local.clearFieldErrors) {
        updateItemIn(
          state.page.item,
          { id: state.local.clearFieldErrors },
          { errors: [] }
        )
        delete state.local.clearFieldErrors
      }
      break

    case 'spouse-details':
      ;[
        'spouse-identification',
        'spouse-contact-details',
        'spouse-employment-details',
      ].forEach((section) => {
        const tmpl = form[section] || {}
        if (!state.form[section] || typeof state.form[section] !== 'object') {
          state.form[section] = { ...tmpl }
        } else {
          Object.keys(tmpl).forEach((k) => {
            if (!(k in state.form[section])) {
              state.form[section][k] = tmpl[k]
            }
          })
        }
        const sectionObj =
          state.form &&
          state.form[section] &&
          typeof state.form[section] === 'object'
            ? state.form[section]
            : {}
        Object.keys(sectionObj).forEach((field) =>
          updateItemIn(
            state.page.item,
            { id: field },
            { content: sectionObj[field] || '' }
          )
        )
      })
      updateItemIn(
        state.page.item,
        { id: 'spouse-is-trustee' },
        { content: state.form['spouse-is-trustee'] || '' }
      )

      updateItemIn(
        state.page.item,
        { id: 'spouse-funeral' },
        { content: state.form['spouse-funeral'] || '' }
      )

      updateItemIn(
        state.page.item,
        { uid: 'ca82e73a33b2c72b', type: 'list' },
        {
          invisible: state.form['funeral-cover-options']['no-option'] === 'Y',
        }
      )
      updateItemIn(
        state.page.item,
        { uid: '178bc7db0a5b64ba', type: 'html' },
        {
          invisible: state.form['funeral-cover-options']['no-option'] === 'Y',
        }
      )
      updateItemIn(
        state.page.item,
        { uid: '93ad48174469aa41', type: 'list' },
        {
          invisible: state.form['funeral-cover-options']['no-option'] === 'Y',
        }
      )

      const otherLives = (state.form['additional-lives'] || []).length

      updateItemIn(
        state.page.item,
        { uid: 'c69240fbf3b4768d', type: 'table' },
        {
          triggers: ['DoubleClick'],
          invisibile: otherLives < 1,
          content: (state.form['additional-lives'] || []).map(
            (life, idx, arr) => otherLivesItem(life, idx, arr)
          ),
        }
      )

      //Hide life assured button if no lives has been added
      updateItemIn(
        state.page.item,
        { uid: '949a59451e9c6919', type: 'list' },
        {
          invisible: otherLives < 1,
        }
      )

      break

    case 'other-lives-assured':
      const numOtherLives = (state.form['additional-lives'] || []).length

      updateItemIn(
        state.page.item,
        { id: 'other-lives-assured', type: 'table' },
        {
          triggers: ['DoubleClick'],
          invisibile: numOtherLives < 1,
          content: (state.form['additional-lives'] || []).map(
            (life, idx, arr) => otherLivesItem(life, idx, arr)
          ),
        }
      )

      updateItemIn(
        state.page.item,
        { id: 'system-constant', uid: '32bfaf422b20744e' },
        { invisible: numOtherLives >= 2 }
      )

      state.page.buttons.forEach((button) => {
        switch (button.id) {
          case 'add-other-life-assured':
            button.invisible = numOtherLives >= 6
            break
        }
      })
      break

    case 'add-other-lives':
    case 'edit-other-lives':
      Object.keys(state.local || {}).forEach((field) =>
        updateItemIn(
          state.page.item,
          { id: field },
          {
            content: state.local[field] || '',
          }
        )
      )
      break
    case 'trustee-details':
      ;['trustee-identification', 'trustee-contact-details'].forEach(
        (section) =>
          Object.keys(state.form[section]).forEach((field) =>
            updateItemIn(
              state.page.item,
              { id: field },
              { content: state.form[section][field] || '' }
            )
          )
      )
      break

    case 'additional-lives': {
      syncSpouseAdditionalLifeFromQuote(state)
      const lives = state.form['additional-lives'] || []
      lives.forEach((life) => {
        ;['first-names', 'surname', 'date-of-birth'].forEach((k) => {
          if (!(k in life)) life[k] = null
        })
      })
      updateItemIn(
        state.page.item,
        { id: 'additional-lives' },
        {
          content: additionalLivesDetailsTablePrefix.concat(
            lives.map((life, idx, arr) =>
              additionalLifeDetailsItem(life, idx, arr)
            )
          ),
        }
      )
      validate(
        state,
        state.local && state.local.failed_validation ? false : true
      )
      syncQuotationPanelValues(state)
      break
    }

    case 'beneficiary-details':
      const numBeneficiaries = (state.form['beneficiary-details'] || []).length
      updateItemIn(
        state.page.item,
        { id: 'beneficiary-details', type: 'table' },
        {
          invisible: numBeneficiaries < 1,
          content: (state.form['beneficiary-details'] || []).map(
            beneficiaryItem
          ),
        }
      )

      updateItemIn(
        state.page.item,
        { id: 'system-field-outcome', uid: '6c60b1c422ae8373' }, // Add Trustee as beneficiary
        {
          invisible: isTrusteeABeneficiary(state.form) || numBeneficiaries >= 9,
        }
      )

      updateItemIn(
        state.page.item,
        { id: 'system-field-outcome', uid: 'fdd3e9a355e983ef' }, // Add additional life as beneficiary
        { invisible: numBeneficiaries >= 9 }
      )

      updateItemIn(
        state.page.item,
        { id: 'system-field-outcome', uid: '145b199f80e0c8c3' }, // Add a different beneficiary
        { invisible: numBeneficiaries >= 9 }
      )

      state.page.buttons.forEach((button) => {
        switch (button.id) {
          case 'add-another-beneficiary':
            button.invisible = numBeneficiaries >= 9
            break
          case 'add-trustee':
            button.invisible =
              isTrusteeABeneficiary(state.form) || numBeneficiaries >= 9
            break
          case 'update-benefit-split-percentage':
            button.invisible = numBeneficiaries < 2
            break
        }
      })
      break

    case 'add-beneficiary':
      Object.keys(state.local || {}).forEach((field) =>
        updateItemIn(
          state.page.item,
          { id: field },
          {
            content: state.local[field] || '',
          }
        )
      )
      break
    case 'edit-beneficiary':
      Object.keys(state.local || {}).forEach((field) =>
        updateItemIn(
          state.page.item,
          { id: field },
          {
            content: state.local[field] || '',
          }
        )
      )
      break

    case 'update-beneficiary-split-percentage':
      Object.keys(state.local || {}).forEach((field) =>
        updateItemIn(
          state.page.item,
          { id: field },
          {
            content: state.local[field] || '',
          }
        )
      )
      break

    case 'insurance-arrangements':
      Object.keys(state.form['existing-policies']).forEach((field) =>
        updateItemIn(
          state.page.item,
          { id: field },
          { content: state.form['existing-policies'][field] || '' }
        )
      )

      ensureExistingPolicyDetailsArray(state.form)
      ;[1, 2, 3].forEach((ix) => {
        updateItemIn(
          state.page.item,
          { id: `company-name-${ix}` },
          {
            content:
              existingPolicyDetailsFieldValue(state.form, ix, 'company-name') ||
              '',
          }
        )
        updateItemIn(
          state.page.item,
          { id: `existing-contract-id-${ix}` },
          {
            content:
              existingPolicyDetailsFieldValue(
                state.form,
                ix,
                'existing-contract-id'
              ) || '',
          }
        )
        updateItemIn(
          state.page.item,
          { id: `sum-assured-${ix}` },
          {
            content:
              existingPolicyDetailsFieldValue(state.form, ix, 'sum-assured') ||
              '',
          }
        )
      })
      //Hide Elements
      updateItemIn(
        state.page.item,
        { uid: '427309bfc272eea5', type: 'list' },
        {
          invisible: !(
            state.form['existing-policies']['existing-coverage'] === 'Y' ||
            state.form['existing-policies']['application-replaces-existing'] ===
              'Y'
          ),
        }
      )
      break

    case 'medical-underwriting':
      Object.keys(state.form['life-assured-general-habits']).forEach((field) =>
        updateItemIn(
          state.page.item,
          { id: field },
          { content: state.form['life-assured-general-habits'][field] || '' }
        )
      )
      ;[
        'planning-other-insurance',
        'other-assurance-past12-months',
        'risky-occupation-hobbies',
        'refused-application-or-increased-premium',
        'insolvency-issue',
        'occupation-not-clerical',
      ].forEach((field) => {
        updateItemIn(
          state.page.item,
          { id: `${field}-details` },
          {
            invisible:
              state.form['life-assured-general-habits'][field] !== 'Yes',
          }
        )
      })
      ;['5f8b25ed4415c897', 'beaacad6ef6645ee', '91930796f1d9da82'].forEach(
        (field) => {
          updateItemIn(
            state.page.item,
            { uid: field, type: 'list' },
            {
              invisible:
                state.form['life-assured-general-habits'][
                  'cover-exceeds-one-hundred-thousand'
                ] !== 'Yes',
            }
          )
        }
      )

      updateItemIn(
        state.page.item,
        { id: 'years-smoking' },
        { invisible: state.form['life-assured-general-habits'].smoker !== 'Y' }
      )
      updateItemIn(
        state.page.item,
        { id: 'smoking-details', type: 'list' },
        { invisible: state.form['life-assured-general-habits'].smoker !== 'Y' }
      )

      updateItemIn(
        state.page.item,
        { id: 'years-not-smoking' },
        {
          invisible:
            state.form['life-assured-general-habits']['non-smoker'] !== 'Y',
        }
      )

      Object.keys(state.form['life-assured-health-details']).forEach((field) =>
        updateItemIn(
          state.page.item,
          { id: field },
          { content: state.form['life-assured-health-details'][field] || '' }
        )
      )
      Object.keys(state.form['medical-attendant-details']).forEach((field) =>
        updateItemIn(
          state.page.item,
          { id: field },
          { content: state.form['medical-attendant-details'][field] || '' }
        )
      )
      Object.keys(state.form['life-assured-medical-details']).forEach((field) =>
        updateItemIn(
          state.page.item,
          { id: field },
          { content: state.form['life-assured-medical-details'][field] || '' }
        )
      )

      updateItemIn(
        state.page.item,
        { uid: '9a5a65cc9d9c54d5' },
        {
          invisible:
            state.form['life-assured-health-details'][
              'family-history-of-illness'
            ] !== 'Yes',
        }
      )
      break

    case 'medical-declaration':
      Object.keys(state.form['life-assured-health-details']).forEach((field) =>
        updateItemIn(
          state.page.item,
          { id: field },
          { content: state.form['life-assured-health-details'][field] || '' }
        )
      )
      Object.keys(state.form['medical-attendant-details']).forEach((field) =>
        updateItemIn(
          state.page.item,
          { id: field },
          { content: state.form['medical-attendant-details'][field] || '' }
        )
      )
      Object.keys(state.form['life-assured-medical-details']).forEach((field) =>
        updateItemIn(
          state.page.item,
          { id: field },
          { content: state.form['life-assured-medical-details'][field] || '' }
        )
      )

      updateItemIn(
        state.page.item,
        { uid: '9a5a65cc9d9c54d5' },
        {
          invisible:
            state.form['life-assured-health-details'][
              'family-history-of-illness'
            ] !== 'Yes',
        }
      )
      break

    case 'health-descriptions':
      state.form['health-concerns'].forEach((c, i) =>
        Object.keys(c).forEach((field) =>
          updateItemIn(
            state.page.item,
            { id: `${field}-${i + 1}` },
            { content: c[field] || '' }
          )
        )
      )
      break
    case 'payment-details':
      //premium adjusted by selected payment frequency
      updateItemIn(
        state.page.item,
        { id: 'premium' },
        {
          content: amountString(frequencyAdjustedPremium(state.form) || 0),
        }
      )
      Object.keys(state.form['payment-method-selection']).forEach((field) =>
        updateItemIn(
          state.page.item,
          { id: field, type: 'field' },
          { content: state.form['payment-method-selection'][field] || '' }
        )
      )
      const months = startOfNextMonths(3)
      if (!state.form['payment-start-date']) {
        state.form['payment-start-date'] = dayjs(months[0]).format('YYYYMMDD')
      }
      updateItemIn(
        state.page.item,
        { id: 'payment-start-date' },
        {
          content: dayjs(state.form['payment-start-date']).format(dateFormat),
          options: months,
        }
      )
      //Payment Frequency
      Object.keys(state.form['payment-frequency']).forEach((field) =>
        updateItemIn(
          state.page.item,
          { id: field },
          { content: state.form['payment-frequency'][field] || '' }
        )
      )

      //Payer person
      Object.keys(state.form['payer-person']).forEach((field) =>
        updateItemIn(
          state.page.item,
          { id: field },
          { content: state.form['payer-person'][field] || '' }
        )
      )

      //Hide radio options when not married
      updateItemIn(
        state.page.item,
        { uid: 'ce35234c9c489acc', type: 'list' },
        {
          invisible:
            state.form['life-assured-identification']['marital-status'] !==
            'Married',
        }
      )
      break
    case 'debit-order-details':
      updateItemIn(
        state.page.item,
        { id: 'premium' },
        {
          content: amountString(frequencyAdjustedPremium(state.form) || 0),
        }
      )
      Object.keys(state.form['payment-bank-details']).forEach((field) =>
        updateItemIn(
          state.page.item,
          { id: field },
          { content: state.form['payment-bank-details'][field] || '' }
        )
      )
      break
    case 'stop-order-details':
      updateItemIn(
        state.page.item,
        { id: 'premium' },
        {
          content: amountString(frequencyAdjustedPremium(state.form) || 0),
        }
      )
      Object.keys(state.form['payment-employment-details']).forEach((field) =>
        updateItemIn(
          state.page.item,
          { id: field },
          { content: state.form['payment-employment-details'][field] || '' }
        )
      )
      break

    case 'forces-pay-office-additional-information': {
      ensureForcesPayOfficeDetails(state)
      const todayFPO = dayjs().format('YYYYMMDD')
      const personnelSection = findItemsIn(state.page.item, {
        id: 'personnel-information',
        type: 'list',
      })
      if (personnelSection.length > 0) {
        updateItemIn(
          personnelSection[0],
          { id: 'first-names' },
          {
            content:
              state.form['life-assured-identification']['first-names'] || '',
          }
        )
        updateItemIn(
          personnelSection[0],
          { id: 'surname' },
          { content: state.form['life-assured-identification'].surname || '' }
        )
        updateItemIn(
          personnelSection[0],
          { id: 'svc-number' },
          {
            content:
              state.form['forces-pay-office-details']['svc-number'] || '',
          }
        )
        updateItemIn(
          personnelSection[0],
          { id: 'armed-forces-rank' },
          {
            content:
              state.form['forces-pay-office-details']['armed-forces-rank'] ||
              '',
            markers: ['*'],
          }
        )
        updateItemIn(
          personnelSection[0],
          { id: 'unit' },
          {
            content: state.form['forces-pay-office-details'].unit || '',
            markers: ['*'],
          }
        )

        const isGhanaCard =
          state.form['life-assured-identification']['nia-ghana-card'] === 'Y'
        const idNumber =
          state.form['life-assured-identification']['idpassport-number']

        if (isGhanaCard && idNumber) {
          state.form['forces-pay-office-details']['ecowas-id-number'] = idNumber
        }

        if (
          findItemsIn(personnelSection[0], { id: 'ecowas-id-number' }).length >
          0
        ) {
          if (isGhanaCard) {
            updateItemIn(
              personnelSection[0],
              { id: 'ecowas-id-number' },
              {
                content:
                  state.form['forces-pay-office-details']['ecowas-id-number'] ||
                  idNumber ||
                  '',
                readonly: true,
                errors: [],
              }
            )
          } else {
            updateItemIn(
              personnelSection[0],
              { id: 'ecowas-id-number' },
              {
                content:
                  state.form['forces-pay-office-details']['ecowas-id-number'] ||
                  '',
                readonly: false,
                errors: ['*'],
              }
            )
          }
        }

        updateItemIn(
          personnelSection[0],
          { id: 'cell-phone' },
          {
            content:
              state.form['life-assured-contact-details']['cell-phone'] || '',
          }
        )
        updateItemIn(personnelSection[0], { id: 'date' }, { content: todayFPO })
      }

      const insuranceSection = findItemsIn(state.page.item, {
        id: 'insurance-details',
        type: 'list',
      })
      if (insuranceSection.length > 0) {
        updateItemIn(
          insuranceSection[0],
          { id: 'policy-name' },
          {
            content:
              state.form['forces-pay-office-details']['policy-name'] ||
              'Executive Plus Revised',
          }
        )

        const paymentFrequency = state.form['payment-frequency']
        const selectedFrequency = Object.keys(paymentFrequency).find(
          (key) => paymentFrequency[key] === 'Y'
        )
        const frequencyLabel = selectedFrequency
          ? selectedFrequency.charAt(0).toUpperCase() +
            selectedFrequency.slice(1).replace(/-/g, ' ')
          : ''
        updateItemIn(
          insuranceSection[0],
          { id: 'frequency' },
          { content: frequencyLabel }
        )
        updateItemIn(
          insuranceSection[0],
          { id: 'date-of-first-deduction' },
          {
            content: state.form['payment-start-date']
              ? dayjs(state.form['payment-start-date']).format('YYYYMMDD')
              : todayFPO,
          }
        )
      }

      const companySection = findItemsIn(state.page.item, {
        id: 'company-details',
        type: 'list',
      })
      if (companySection.length > 0) {
        updateItemIn(
          companySection[0],
          { id: 'company-name' },
          {
            content:
              state.form['forces-pay-office-details']['company-name'] ||
              'Enterprise Life Assurance Co Ltd',
          }
        )
        updateItemIn(
          companySection[0],
          { id: 'branch-name' },
          {
            content:
              state.form['forces-pay-office-details']['branch-name'] ||
              'High Street',
          }
        )
        updateItemIn(companySection[0], { id: 'date' }, { content: todayFPO })
        updateItemIn(
          companySection[0],
          { id: 'representative-name' },
          {
            content:
              state.form['forces-pay-office-details']['representative-name'] ||
              'Justice Asenso Buabeng',
          }
        )
        updateItemIn(
          companySection[0],
          { id: 'telephone-number' },
          {
            content:
              state.form['forces-pay-office-details']['telephone-number'] ||
              '0307084444',
          }
        )
      }
      break
    }

    case 'payment-details-verification': {
      ensureForcesPayOfficeDetails(state)
      const todayPDV = dayjs().format('YYYYMMDD')

      const payerEmploymentSection = findItemsIn(state.page.item, {
        id: 'payer-employment-details',
        type: 'list',
      })
      if (payerEmploymentSection.length > 0) {
        updateItemIn(
          payerEmploymentSection[0],
          { id: 'employer-organisation' },
          {
            content:
              state.form['payment-employment-details'][
                'employer-organisation'
              ] || '',
          }
        )
        updateItemIn(
          payerEmploymentSection[0],
          { id: 'employee-number' },
          {
            content:
              state.form['payment-employment-details']['employee-number'] || '',
          }
        )
        updateItemIn(
          payerEmploymentSection[0],
          { id: 'paypoint-contract-id' },
          { content: 'GH1P0000330' }
        )
      }

      const fpoDetailsSection = findItemsIn(state.page.item, {
        id: 'forces-pay-office-details',
        type: 'list',
      })
      if (fpoDetailsSection.length > 0) {
        updateItemIn(
          fpoDetailsSection[0],
          { id: 'cell-phone' },
          {
            content:
              state.form['life-assured-contact-details']['cell-phone'] || '',
          }
        )
        updateItemIn(
          fpoDetailsSection[0],
          { id: 'date' },
          { content: todayPDV }
        )
        updateItemIn(
          fpoDetailsSection[0],
          { id: 'first-names' },
          {
            content:
              state.form['life-assured-identification']['first-names'] || '',
          }
        )
        updateItemIn(
          fpoDetailsSection[0],
          { id: 'surname' },
          { content: state.form['life-assured-identification'].surname || '' }
        )
        updateItemIn(
          fpoDetailsSection[0],
          { id: 'premium-amount' },
          { content: formattedPremiumForFpo(state.form) }
        )
        const pdvPaymentFrequency = state.form['payment-frequency']
        const pdvSelectedFrequency = Object.keys(pdvPaymentFrequency).find(
          (key) => pdvPaymentFrequency[key] === 'Y'
        )
        const pdvFrequencyLabel = pdvSelectedFrequency
          ? pdvSelectedFrequency.charAt(0).toUpperCase() +
            pdvSelectedFrequency.slice(1).replace(/-/g, ' ')
          : ''
        updateItemIn(
          fpoDetailsSection[0],
          { id: 'frequency' },
          { content: pdvFrequencyLabel }
        )
        updateItemIn(
          fpoDetailsSection[0],
          { id: 'date-of-first-deduction' },
          {
            content: state.form['payment-start-date']
              ? dayjs(state.form['payment-start-date']).format('YYYYMMDD')
              : todayPDV,
          }
        )
        updateItemIn(
          fpoDetailsSection[0],
          { id: 'company-name' },
          {
            content:
              state.form['forces-pay-office-details']['company-name'] ||
              'Enterprise Life Assurance Co Ltd',
          }
        )
        updateItemIn(
          fpoDetailsSection[0],
          { id: 'branch-name' },
          {
            content:
              state.form['forces-pay-office-details']['branch-name'] ||
              'High Street',
          }
        )
        updateItemIn(
          fpoDetailsSection[0],
          { id: 'company-date' },
          {
            content: state.form['forces-pay-office-details'].date || todayPDV,
          }
        )
        updateItemIn(
          fpoDetailsSection[0],
          { id: 'representative-name' },
          {
            content:
              state.form['forces-pay-office-details']['representative-name'] ||
              'Justice Asenso Buabeng',
          }
        )
        updateItemIn(
          fpoDetailsSection[0],
          { id: 'telephone-number' },
          {
            content:
              state.form['forces-pay-office-details']['telephone-number'] ||
              '0307084444',
          }
        )
        updateItemIn(
          fpoDetailsSection[0],
          { id: 'company-signed-date' },
          { content: todayPDV }
        )
        updateItemIn(
          fpoDetailsSection[0],
          { id: 'signed-date' },
          { content: todayPDV }
        )
        updateItemIn(
          fpoDetailsSection[0],
          { id: 'svc-number' },
          {
            content:
              state.form['forces-pay-office-details']['svc-number'] || '',
          }
        )
        updateItemIn(
          fpoDetailsSection[0],
          { id: 'armed-forces-rank' },
          {
            content:
              state.form['forces-pay-office-details']['armed-forces-rank'] ||
              '',
          }
        )
        updateItemIn(
          fpoDetailsSection[0],
          { id: 'unit' },
          { content: state.form['forces-pay-office-details'].unit || '' }
        )
      }

      const signatureSection = findItemsIn(state.page.item, {
        id: 'signature-section',
        type: 'list',
      })
      if (signatureSection.length > 0) {
        const hasSignatureError =
          state.local &&
          state.local.failed_validation &&
          !state.form['payer-signature']
        updateItemIn(
          signatureSection[0],
          { id: 'interactive-canvas-html' },
          {
            errors: hasSignatureError ? ['Your signature is required'] : [],
          }
        )
      }
      break
    }

    case 'upload-documents':
      state.page.buttons.forEach((button) => {
        if (button.id === 'upload-service-certificate-document') {
          button.invisible = !isForcesPayOffice(state)
        }
      })
      updateItemIn(
        state.page.item,
        { id: 'system-field-outcome', uid: 'fisp-svc-cert-link' },
        { invisible: !isForcesPayOffice(state) }
      )
      if (isForcesPayOffice(state)) {
        const serviceCert =
          state.form.documents &&
          state.form.documents['service-certificate-document']
        const hasServiceCert =
          serviceCert && Array.isArray(serviceCert) && serviceCert.length > 0
        const hasError =
          state.local && state.local.failed_validation && !hasServiceCert
        updateItemIn(
          state.page.item,
          { id: 'service-certificate-error' },
          {
            invisible: !hasError,
          }
        )
      }
      break

    case 'upload-document-pages':
      state.local.pages.forEach((doc, idx) =>
        updateItemIn(
          state.page.item,
          { id: `upload-${idx + 1}` },
          { content: doc }
        )
      )
      break

    case 'acceptance-screen':
      generateAcceptancePage(state)
      break
  }
}

const createState = (state) => {
  state.currentProcess = {}
  state.currentProcess.form = Object.assign(
    { 'reference-number': shortid.generate() },
    form
  )
  state.currentProcess.step = 'quotation-details'
  state.currentProcess.page = pages['quotation-details']
  state.currentProcess.local = null
  setPageItemFromState(state.currentProcess)
}

// processButton
const processButton = (state, button, asyncDispatch) => {
  if (button.id == 'cancel') {
    const step = state.step
    changePage(state, 'confirm-cancel')
    state.local = state.local || {}
    state.local.previous = step
  } else {
    switch (state.step) {
      case 'confirm-cancel':
        switch (button.id) {
          case 'yes':
            asyncDispatch(cancelProcess(state['process-id']))
            break
          case 'no':
            changePage(state, state.local.previous)
            break
        }
        break
      case 'quotation-details':
        switch (button.id) {
          case 'next':
            if (validate(state)) {
              changePage(state, 'additional-benefits')
            }
            break
        }
        break
      case 'additional-benefits':
        switch (button.id) {
          case 'back':
            changePage(state, 'quotation-details')
            break
          case 'next':
            if (validate(state)) {
              changePage(state, 'quotation-family')
            }
            break
        }
        break

      case 'quotation-family':
        switch (button.id) {
          case 'back':
            changePage(state, 'additional-benefits')
            break
          case 'next':
            changePage(state, 'personal-details')
            break
          case 'add-family': {
            changePage(state, 'add-family-members')
            state.local = { mode: 'add' }
            break
          }
          case 'edit-family': {
            const lives = state.form['additional-lives'] || []
            if (lives.length < 1) break
            changePage(state, 'edit-family-members')
            state.local = { mode: 'edit' }
            break
          }
          case 'remove-family': {
            const livesRm = state.form['additional-lives'] || []
            if (livesRm.length < 1) break
            changePage(state, 'remove-family-members')
            state.local = { mode: 'remove' }
            break
          }
        }
        break

      case 'add-family-members':
        if (button.id === 'back') {
          const loc = state.local || {}
          if (loc.relationshipCategory) {
            delete loc.relationshipCategory
          } else {
            changePage(state, 'quotation-family')
            state.local = null
          }
        } else {
          const mlAge = mainLifeAge(state.form) || 0
          let optionLevel = additionalLifeFamilyOptions(mlAge)
          const loc = state.local || {}
          if (
            loc.relationshipCategory &&
            additionalLifeFamilyLabels.indexOf(loc.relationshipCategory) >= 0
          ) {
            optionLevel = optionLevel[loc.relationshipCategory]
          }
          const elem = Object.keys(optionLevel).find(
            (label) => button.id === slugify(label)
          )
          if (elem) {
            const node = optionLevel[elem]
            const mainG = state.form['life-assured-identification'].gender
            if (node && node.options) {
              const gg = applyAdditionalLifeGenderForRelationship(
                elem,
                mainG,
                state.form['joint-life'] === 'Y'
              )
              const ageSt = spouseAdditionalLifeAgeState(
                state.form,
                elem,
                node.options
              )
              changePage(state, 'family-members-age')
              state.local = {
                mode: 'add',
                relationship: elem,
                relationshipCategory: loc.relationshipCategory || null,
                ageOptions: ageSt.ageOptions,
                'age-of-member': ageSt['age-of-member'],
                ageLocked: ageSt.ageLocked,
                gender: gg.gender,
                genderLocked: gg.genderLocked,
                'funeral-cover': 'N',
                'transition-cover': 'N',
                'old-age-cover': 'N',
              }
            } else if (node && typeof node === 'object') {
              state.local = state.local || {}
              state.local.mode = 'add'
              state.local.relationshipCategory = elem
            }
          }
        }
        break

      case 'edit-family-members':
        if (button.id === 'back') {
          changePage(state, 'quotation-family')
          state.local = null
        } else if (typeof button.index === 'number') {
          gotoEditAdditionalLife(state, button.index)
        }
        break

      case 'remove-family-members':
        if (typeof button.index === 'number') {
          const old = original(state.form['additional-lives'])
          state.form['additional-lives'] = old
            .slice(0, button.index)
            .concat(old.slice(button.index + 1, old.length))
          changePage(state, 'quotation-family')
        } else if (button.id === 'back') {
          changePage(state, 'quotation-family')
        }
        break

      case 'family-members-age':
        if (button.id === 'delete-this-additional-life') {
          const delLoc = state.local || {}
          if (delLoc.mode === 'edit' && typeof delLoc.index === 'number') {
            const cur = original(state.form['additional-lives'] || [])
            const ix = delLoc.index
            if (ix >= 0 && ix < cur.length) {
              state.form['additional-lives'] = cur
                .slice(0, ix)
                .concat(cur.slice(ix + 1))
            }
            changePage(state, 'quotation-family')
            state.local = null
          }
          break
        }
        switch (button.id) {
          case 'back':
            if (state.local && state.local.mode === 'edit') {
              changePage(state, 'quotation-family')
              state.local = null
            } else {
              changePage(state, 'add-family-members')
              state.local = { mode: 'add' }
            }
            break
          case 'next':
            if (validate(state)) {
              // enforce transition cover availability
              if (!state.form['transition-plan']) {
                state.local['transition-cover'] = 'N'
              }
              // enforce old-age option applicability
              if (
                !eligibleForOldAgeCover(state.form, state.local.relationship)
              ) {
                state.local['old-age-cover'] = 'N'
              }
              const localAfterAge = state.local
              changePage(state, 'family-members-quotation-details')
              state.local = localAfterAge
            }
            break
        }
        break

      case 'family-members-quotation-details':
        if (button.id === 'delete-this-additional-life') {
          const dl = state.local || {}
          if (dl.mode === 'edit' && typeof dl.index === 'number') {
            const cur = original(state.form['additional-lives'] || [])
            const ix = dl.index
            if (ix >= 0 && ix < cur.length) {
              state.form['additional-lives'] = cur
                .slice(0, ix)
                .concat(cur.slice(ix + 1))
            }
            changePage(state, 'quotation-family')
            state.local = null
          }
          break
        }
        switch (button.id) {
          case 'back':
            const localBeforeQuotation = state.local
            changePage(state, 'family-members-age')
            state.local = localBeforeQuotation
            break
          case 'next':
            if (validate(state)) {
              const record = {
                relationship: state.local.relationship,
                'age-of-member': state.local['age-of-member'],
                gender: state.local.gender,
                'funeral-cover': state.local['funeral-cover'],
                'transition-cover': state.local['transition-cover'],
                'old-age-cover': state.local['old-age-cover'],
                'funeral-cover-benefit':
                  state.local['funeral-cover-benefit'] || null,
                'funeral-cover-term': state.local['funeral-cover-term'] || null,
                'memorial-seven-day': state.local['memorial-seven-day'] || 'N',
                'memorial-forty-day': state.local['memorial-forty-day'] || 'N',
                'memorial-one-year': state.local['memorial-one-year'] || 'N',
                'old-age-cover-benefit':
                  state.local['old-age-cover-benefit'] || null,
              }
              if (
                state.local.mode === 'edit' &&
                typeof state.local.index === 'number'
              ) {
                const prev = original(
                  state.form['additional-lives'][state.local.index]
                )
                state.form['additional-lives'][state.local.index] =
                  Object.assign({}, prev, record)
              } else if (state.form['additional-lives'].length < 6) {
                state.form['additional-lives'].push(record)
              }
              changePage(state, 'quotation-family')
              state.local = null
            }
            break
        }
        break
      case 'personal-details':
        switch (button.id) {
          case 'back':
            changePage(state, 'quotation-family')
            break
          case 'next':
            if (validate(state)) {
              if (state.form['spouse-is-trustee'] === 'Y') {
                ;['identification', 'contact-details'].forEach((section) =>
                  Object.keys(state.form[`spouse-${section}`]).forEach(
                    (field) =>
                      (state.form[`trustee-${section}`][field] =
                        state.form[`spouse-${section}`][field])
                  )
                )
                state.form['trustee-identification'].relationship = 'Spouse'
              }
              if (
                state.form['life-assured-identification']['marital-status'] ===
                  'Married' &&
                state.form['joint-life'] === 'Y'
              ) {
                changePage(state, 'spouse-details')
              } else {
                changePage(state, 'trustee-details')
              }
            }
            if (
              state.form['life-assured-identification']['marital-status'] !==
              'Married'
            ) {
              state.form['payer-person']['myself'] = 'Y'
            }
            break
        }
        break
      case 'spouse-details':
        switch (button.id) {
          case 'back':
            changePage(state, 'personal-details')
            break
          case 'next':
            if (validate(state)) {
              if (state.form['spouse-is-trustee'] === 'Y') {
                ;['identification', 'contact-details'].forEach((section) =>
                  Object.keys(state.form[`spouse-${section}`]).forEach(
                    (field) =>
                      (state.form[`trustee-${section}`][field] =
                        state.form[`spouse-${section}`][field])
                  )
                )
                state.form['trustee-identification'].relationship = 'Spouse'
              }
              changePage(state, 'trustee-details')
            }
            break
          case 'add-other-life-assured':
            if (state.form['additional-lives'].length < 6) {
              changePage(state, 'add-other-lives')
              state.local = [
                'first-names',
                'surname',
                'date-of-birth',
                'gender',
                'role',
              ].reduce((l, f) => {
                l[f] = null
                return l
              }, {})
            }
            break
        }
        break
      case 'other-lives-assured':
        switch (button.id) {
          case 'back':
            changePage(state, 'additional-benefits')
            break
          case 'next':
            changePage(state, 'personal-details')
            break
          case 'add-other-life-assured':
            if (state.form['additional-lives'].length < 6) {
              changePage(state, 'add-other-lives')
              state.local = [
                'first-names',
                'surname',
                'date-of-birth',
                'gender',
                'role',
              ].reduce((l, f) => {
                l[f] = null
                return l
              }, {})
            }
            break
        }
        break
      case 'add-other-lives':
        switch (button.id) {
          case 'back':
            changePage(
              state,
              state.form['life-assured-identification']['marital-status'] ===
                'Married'
                ? 'spouse-details'
                : 'other-lives-assured'
            )
            break
          case 'next':
            if (validate(state)) {
              if (state.form['additional-lives'].length < 6) {
                state.form['additional-lives'].push({
                  'first-names': state.local['first-names'],
                  surname: state.local.surname,
                  'date-of-birth': state.local['date-of-birth'],
                  role: state.local.role || null,
                  gender: state.local.gender,
                })
              }
              changePage(
                state,
                state.form['life-assured-identification']['marital-status'] ===
                  'Married'
                  ? 'spouse-details'
                  : 'other-lives-assured'
              )
            }
            break
        }
        break
      case 'edit-other-lives':
        switch (button.id) {
          case 'update':
            if (
              state.local.index >= 0 &&
              state.local.index <= state.form['additional-lives'].length
            ) {
              Object.keys(
                state.form['additional-lives'][state.local.index - 1]
              ).forEach(
                (k) =>
                  (state.form['additional-lives'][state.local.index - 1][k] =
                    state.local[k])
              )
            }
            if (
              state.form['life-assured-identification']['marital-status'] ===
              'Married'
            ) {
              changePage(state, 'spouse-details')
            } else {
              changePage(state, 'other-lives-assured')
            }
            break
          case 'delete-member':
            if (
              state.local.index >= 0 &&
              state.local.index <= state.form['additional-lives'].length
            ) {
              const old = original(state.form['additional-lives'])
              state.form['additional-lives'] = old
                .slice(0, state.local.index - 1)
                .concat(old.slice(state.local.index, old.length))
            }
            if (
              state.form['life-assured-identification']['marital-status'] ===
              'Married'
            ) {
              changePage(state, 'spouse-details')
            } else {
              changePage(state, 'other-lives-assured')
            }
            break
          case 'back':
            if (
              state.form['life-assured-identification']['marital-status'] ===
              'Married'
            ) {
              changePage(state, 'spouse-details')
            } else {
              changePage(state, 'other-lives-assured')
            }
            break
        }
        break
      case 'trustee-details':
        switch (button.id) {
          case 'back':
            if (
              state.form['life-assured-identification']['marital-status'] ===
                'Married' &&
              state.form['joint-life'] === 'Y'
            ) {
              changePage(state, 'spouse-details')
            } else {
              changePage(state, 'personal-details')
            }
            break
          case 'next':
            if (validate(state)) {
              changePage(
                state,
                (state.form['additional-lives'] || []).length > 0
                  ? 'additional-lives'
                  : 'beneficiary-details'
              )
            }
            break
        }
        break

      case 'additional-lives':
        switch (button.id) {
          case 'next':
            if (validate(state)) {
              changePage(state, 'beneficiary-details')
            }
            break
          case 'back':
            changePage(state, 'trustee-details')
            break
        }
        break

      case 'beneficiary-details':
        switch (button.id) {
          case 'back':
            if ((state.form['additional-lives'] || []).length > 0) {
              changePage(state, 'additional-lives')
            } else {
              changePage(state, 'trustee-details')
            }
            break
          case 'next':
            if (state.form['beneficiary-details'].length < 1) {
              changePage(state, 'beneficiary-required')
            } else {
              changePage(state, 'insurance-arrangements')
            }
            break
          case 'add-trustee':
            if (
              state.form['beneficiary-details'].length < 9 &&
              !isTrusteeABeneficiary(original(state.form))
            ) {
              state.form['beneficiary-details'].push({
                'first-names':
                  state.form['trustee-identification']['first-names'],
                surname: state.form['trustee-identification'].surname,
                'benefit-split': null,
                role: 'Cessionary',
                gender: state.form['trustee-identification'].gender,
                'date-of-birth':
                  state.form['trustee-identification']['date-of-birth'],
              })
              updateBeneficiarySplit(state.form)
            }
            break

          case 'add-another-beneficiary':
            if (state.form['beneficiary-details'].length < 9) {
              changePage(state, 'add-beneficiary')
              state.local = [
                'first-names',
                'surname',
                'date-of-birth',
                'role',
                'gender',
              ].reduce((l, f) => {
                l[f] = null
                return l
              }, {})
            }
            break

          case 'add-life-assured-as-beneficiary': {
            const lives = state.form['additional-lives'] || []
            const beneficiaries = state.form['beneficiary-details'] || []
            const btns = []
            lives.forEach((life, i) => {
              if (!isAddedLivesBeneficiary(life, beneficiaries)) {
                btns.push(
                  createButton(life['first-names'] || 'Life', { index: i })
                )
              }
            })
            if (btns.length < 1) {
              changePage(state, 'additional-life-required')
            } else {
              changePage(state, 'add-other-lives-as-beneficiary')
              state.page.buttons = btns
            }
            break
          }

          case 'update-benefit-split-percentage':
            changePage(state, 'update-beneficiary-split-percentage')
            state.local = state.form['beneficiary-details'].reduce(
              (l, b, c) => {
                l[`beneficiary-split-${c + 1}`] = b['benefit-split'] || ''
                return l
              },
              {}
            )
            updateItemIn(
              state.page.item,
              { id: 'update-benefit-split-percentage', type: 'list' },
              {
                content:
                  state.form['beneficiary-details'].map(benefitSplitItem),
              }
            )
            break
        }
        break

      case 'add-beneficiary':
        switch (button.id) {
          case 'back':
            changePage(state, 'beneficiary-details')
            break
          case 'add':
            if (validate(state)) {
              if (state.form['beneficiary-details'].length < 9) {
                state.form['beneficiary-details'].push({
                  'first-names': state.local['first-names'],
                  surname: state.local.surname,
                  'benefit-split': null,
                  role: state.local.role,
                  gender: state.local.gender,
                  'date-of-birth': state.local['date-of-birth'],
                })
                updateBeneficiarySplit(state.form)
              }
              changePage(state, 'beneficiary-details')
            }
            break
        }
        break

      case 'add-other-lives-as-beneficiary':
        if (
          button.index >= 0 &&
          button.index < state.form['additional-lives'].length
        ) {
          state.form['beneficiary-details'].push({
            'first-names':
              state.form['additional-lives'][button.index]['first-names'],
            surname: state.form['additional-lives'][button.index].surname,
            'benefit-split': null,
            role: state.form['additional-lives'][button.index].role,
            gender: state.form['additional-lives'][button.index].gender,
            'date-of-birth':
              state.form['additional-lives'][button.index]['date-of-birth'],
          })
          updateBeneficiarySplit(state.form)
        }
        changePage(state, 'beneficiary-details')
        break
      case 'edit-beneficiary':
        switch (button.id) {
          case 'update':
            if (validate(state)) {
              if (
                state.local.index >= 0 &&
                state.local.index <= state.form['beneficiary-details'].length
              ) {
                Object.keys(
                  state.form['beneficiary-details'][state.local.index - 1]
                ).forEach(
                  (k) =>
                    (state.form['beneficiary-details'][state.local.index - 1][
                      k
                    ] = state.local[k])
                )
              }
              changePage(state, 'beneficiary-details')
            }
            break
          case 'delete-beneficiary':
            if (
              state.local.index >= 0 &&
              state.local.index <= state.form['beneficiary-details'].length
            ) {
              const old = original(state.form['beneficiary-details'])
              state.form['beneficiary-details'] = old
                .slice(0, state.local.index - 1)
                .concat(old.slice(state.local.index, old.length))
              updateBeneficiarySplit(state.form)
            }
            changePage(state, 'beneficiary-details')
            break
        }
        break

      case 'update-beneficiary-split-percentage':
        switch (button.id) {
          case 'update':
            if (validate(state)) {
              state.form['beneficiary-details'] = original(
                state.form['beneficiary-details']
              ).map((b, c) => {
                b['benefit-split'] = amountString(
                  parseFloat(state.local[`beneficiary-split-${c + 1}`])
                )
                return b
              })
              changePage(state, 'beneficiary-details')
            }
            break
        }
        break

      case 'beneficiary-required':
        switch (button.id) {
          case 'done':
            changePage(state, 'beneficiary-details')
            break
        }
        break
      case 'additional-life-required':
        switch (button.id) {
          case 'done':
            changePage(state, 'beneficiary-details')
            break
        }
        break
      case 'insurance-arrangements':
        switch (button.id) {
          case 'back':
            changePage(state, 'beneficiary-details')
            break
          case 'next':
            changePage(state, 'medical-underwriting')
            break
        }
        break
      case 'medical-underwriting':
        switch (button.id) {
          case 'back':
            changePage(state, 'insurance-arrangements')
            break
          case 'next':
            if (validate(state)) {
              changePage(state, 'medical-declaration')
            }
            break
        }
        break
      case 'medical-declaration':
        switch (button.id) {
          case 'back':
            changePage(state, 'medical-underwriting')
            break
          case 'next':
            if (validate(state)) {
              if (healthConcernCount(original(state.form)) > 0) {
                changePage(state, 'health-descriptions')
                createHealthDetailsPage(state)
              } else {
                state.form['health-concerns'].length = 0
                changePage(state, 'payment-details')
              }
            }
            break
        }
        break
      case 'health-descriptions':
        switch (button.id) {
          case 'back':
            changePage(state, 'medical-declaration')
            break
          case 'next':
            if (validate(state)) {
              changePage(state, 'payment-details')
            }
            break
        }
        break
      case 'payment-details':
        switch (button.id) {
          case 'back':
            if (healthConcernCount(original(state.form)) > 0) {
              changePage(state, 'health-descriptions')
              createHealthDetailsPage(state)
            } else {
              changePage(state, 'medical-declaration')
            }
            break
          case 'next':
            if (state.form['payer-person']['my-spouse'] === 'Y') {
              Object.keys(state.form['payer-person-identification']).forEach(
                (field) =>
                  (state.form['payer-person-identification'][field] =
                    state.form['spouse-identification'][field])
              )
              if (
                state.form['payment-method-selection']['debit-order'] === 'Y'
              ) {
                changePage(state, 'debit-order-details')
              } else if (
                state.form['payment-method-selection']['stop-order'] === 'Y'
              ) {
                changePage(state, 'stop-order-details')
              }
            } else if (state.form['payer-person']['myself'] === 'Y') {
              Object.keys(state.form['payer-person-identification']).forEach(
                (field) =>
                  (state.form['payer-person-identification'][field] =
                    state.form['life-assured-identification'][field])
              )
              if (
                state.form['payment-method-selection']['debit-order'] === 'Y'
              ) {
                changePage(state, 'debit-order-details')
              } else if (
                state.form['payment-method-selection']['stop-order'] === 'Y'
              ) {
                changePage(state, 'stop-order-details')
              }
            }
            break
        }
        break
      case 'debit-order-details':
        switch (button.id) {
          case 'back':
            changePage(state, 'payment-details')
            break
          case 'next':
            if (validate(state)) {
              changePage(state, 'upload-documents')
            }
            break
        }
        break
      case 'stop-order-details':
        switch (button.id) {
          case 'back':
            changePage(state, 'payment-details')
            break
          case 'next':
            if (validate(state)) {
              if (isForcesPayOffice(state)) {
                ensureForcesPayOfficeDetails(state)
                const fp = state.form['forces-pay-office-details']
                fp['company-name'] = 'Enterprise Life Assurance Co Ltd'
                fp['branch-name'] = 'High Street'
                fp.date = dayjs().format('YYYYMMDD')
                fp['representative-name'] = 'Justice Asenso Buabeng'
                fp['telephone-number'] = '0307084444'
                fp['svc-number'] =
                  state.form['payment-employment-details']['employee-number']
                changePage(state, 'forces-pay-office-additional-information')
              } else {
                changePage(state, 'upload-documents')
              }
            }
            break
        }
        break

      case 'forces-pay-office-additional-information':
        switch (button.id) {
          case 'back':
            changePage(state, 'stop-order-details')
            break
          case 'next':
            ensureForcesPayOfficeDetails(state)
            if (validate(state)) {
              changePage(state, 'payment-details-verification')
            }
            break
        }
        break

      case 'payment-details-verification':
        switch (button.id) {
          case 'back':
            changePage(state, 'forces-pay-office-additional-information')
            break
          case 'next':
            if (validate(state)) {
              changePage(state, 'upload-documents')
            } else {
              setPageItemFromState(state)
            }
            break
        }
        break

      case 'upload-documents':
        switch (button.id) {
          case 'upload-id-document':
          case 'upload-service-certificate-document':
            changePage(state, 'upload-document-pages')
            state.local = state.local || {}
            state.local.document = button.id.substring(7)
            if (!state.form.documents[state.local.document]) {
              state.form.documents[state.local.document] = []
            }
            state.local.pages =
              original(state.form.documents[state.local.document]) || []
            const docs =
              state.local.pages.length === 0 ? [null] : state.local.pages
            updateItemIn(
              state.page.item,
              { id: 'uploads' },
              {
                content: docs.map((doc, idx) => {
                  return [
                    createItem('Upload', {
                      component: 'ImageDrop',
                      id: `upload-${idx + 1}`,
                      content: doc,
                      readonly: false,
                      type: 'component',
                    }),
                  ]
                }),
              }
            )
            break
          case 'next':
            if (validate(state)) {
              changePage(state, 'acceptance-screen')
              generateAcceptancePage(state)
            }
            break
          case 'back':
            if (isForcesPayOffice(state)) {
              changePage(state, 'payment-details-verification')
            } else if (
              state.form['payment-method-selection']['debit-order'] === 'Y'
            ) {
              changePage(state, 'debit-order-details')
            } else if (
              state.form['payment-method-selection']['stop-order'] === 'Y'
            ) {
              changePage(state, 'stop-order-details')
            }
            state.form['payer-signature'] = null
            break
        }
        break
      case 'upload-document-pages':
        switch (button.id) {
          case 'continue':
            state.form.documents[state.local.document] = state.local.pages || []
            const savedDoc = state.form.documents[state.local.document]
            const hasUploads = savedDoc && savedDoc.length > 0
            const svcArr = state.form.documents
              ? state.form.documents['service-certificate-document']
              : null
            const hasServiceCertificate =
              svcArr && Array.isArray(svcArr) && svcArr.length > 0
            const fpoMissingServiceCertificate =
              isForcesPayOffice(state) && !hasServiceCertificate

            if (hasUploads && !fpoMissingServiceCertificate) {
              if (state.local) state.local.failed_validation = false
              changePage(state, 'acceptance-screen')
              generateAcceptancePage(state)
            } else {
              if (fpoMissingServiceCertificate && hasUploads) {
                state.local = state.local || {}
                state.local.failed_validation = true
              }
              changePage(state, 'upload-documents')
            }
            break
          case 'abort':
            changePage(state, 'upload-documents')
            break
          case 'add-page':
            const uploadsItem = findItemsIn(state.page.item, { id: 'uploads' })
            if (uploadsItem.length > 0) {
              const currentContent = original(uploadsItem[0].content)
              updateItemIn(
                state.page.item,
                { id: 'uploads' },
                {
                  content: currentContent.concat([
                    [
                      createItem('Upload', {
                        component: 'ImageDrop',
                        id: `upload-${currentContent.length + 1}`,
                        content: null,
                        readonly: false,
                        type: 'component',
                      }),
                    ],
                  ]),
                }
              )
            }
            break
        }
        break
      case 'acceptance-screen':
        switch (button.id) {
          case 'submit':
            if (validate(state)) {
              state.form['application-accepted'] = 'Y'
              state.form['application-acceptance-date'] =
                dayjs().format('YYYYMMDD')
              changePage(state, 'completed')
            }
            break
          case 'back':
            changePage(state, 'upload-documents')
        }
        break
      case 'completed':
        asyncDispatch(completeProcess(state.form['reference-number']))
        break
    }
    setPageItemFromState(state)
  }
}

//processChange
const processChange = (state, data) => {
  let dirty = false
  switch (state.step) {
    case 'quotation-details':
      switch (data.id) {
        case 'date-of-birth':
          if (data.uid === '0d8e0852303cf721') {
            state.form['spouse-identification']['date-of-birth'] = data.value
          } else {
            state.form['life-assured-identification']['date-of-birth'] =
              data.value
          }
          dirty = true
          break
        case 'marital-status':
          state.form['life-assured-identification']['marital-status'] =
            data.value
          dirty = true
          break
        case 'gender':
          state.form['life-assured-identification']['gender'] = data.value
          dirty = true
          break
        case 'joint-life':
          state.form['joint-life'] = data.value
          if (data.value === 'Y') {
            state.form['main-life-only'] = 'N'
          }
          syncAdditionalLivesGenderLocks(state)
          dirty = true
          break
        case 'main-life-only':
          state.form['main-life-only'] = data.value
          if (data.value === 'Y') {
            state.form['joint-life'] = 'N'
            state.form['joint-life-variant'] = null
            state.form['spouse-identification']['date-of-birth'] = null
          }
          dirty = true
          break
        case 'joint-life-variant':
          state.form['joint-life-variant'] = data.value
          dirty = true
          break
        case 'benefit-option':
          state.form['benefit-option'] = data.value
          dirty = true
          break
        case 'update-option':
          state.form['premium-updates'] = data.value
          dirty = true
          break
        case 'death-sum-assured':
        case 'total-personal-disability-sum-assured':
        case 'illness-sum-assured':
        case 'standalone-term':
          state.form['standalone-options'][data.id] = data.value
          dirty = true
          break
        case 'death-and-total-personal-disability':
        case 'death-and-critical-illness':
        case 'death-and-total-disability-and-critical-illness':
        case 'total-personal-disability-and-critical-illness': {
          const keys = [
            'death-and-total-personal-disability',
            'death-and-critical-illness',
            'death-and-total-disability-and-critical-illness',
            'total-personal-disability-and-critical-illness',
          ]
          keys.forEach((k) => {
            state.form['accelerated-options'][k] =
              k === data.id ? data.value : 'N'
          })
          dirty = true
          break
        }
        case 'accelerated-sum-assured':
          state.form['accelerated-options']['accelerated-sum-assured'] =
            data.value
          dirty = true
          break
        case 'accelerated-term':
          state.form['accelerated-options']['accelerated-term'] = data.value
          dirty = true
          break
        case 'funeral-cover-for-main-life':
          state.form['funeral-cover-for-main-life'] = data.value
          dirty = true
          break
      }
      if (data.id === 'marital-status') {
        state.form['spouse-identification'].gender =
          data.value === 'Married'
            ? spouseGender(state.form['life-assured-identification'].gender)
            : null
        state.form['spouse-identification']['marital-status'] =
          data.value === 'Married' ? 'Married' : null

        if (data.value !== 'Married') {
          state.form['joint-life'] = null
          state.form['main-life-only'] = null
          state.form['joint-life-variant'] = null
          state.form['spouse-identification']['date-of-birth'] = null
        }
        syncAdditionalLivesGenderLocks(state)
        dirty = true
      } else if (data.id === 'gender') {
        if (
          state.form['life-assured-identification']['marital-status'] ===
          'Married'
        ) {
          state.form['spouse-identification'].gender = spouseGender(data.value)
          state.form['spouse-identification']['marital-status'] = 'Married'
          syncAdditionalLivesGenderLocks(state)
          dirty = true
        }
      }
      break
    case 'additional-benefits':
      switch (data.id) {
        case 'hospitalisation-units':
          state.form['hospitalisation-units'] = data.value
          dirty = true
          break
        case 'transition-plan':
          state.form['transition-plan'] = data.value
          dirty = true
          break
        case 'personal-accident':
          state.form['personal-accident'] = data.value
          dirty = true
          break
        case 'educational-support-benefit':
        case 'educational-support-term':
          state.form['educational-support-benefit'][data.id] = data.value
          dirty = true
          break
        case 'long-term-savings-premium':
        case 'savings-term':
          state.form['long-term-savings'][data.id] = data.value
          dirty = true
          break
        case 'ghs-5000':
        case 'ghs-7500':
        case 'ghs-10000':
        case 'ghs-15000':
        case 'ghs-20000':
        case 'ghs-25000':
        case 'ghs-30000':
        case 'ghs-40000':
        case 'ghs-50000':
        case 'ghs-60000':
        case 'ghs-70000':
        case 'ghs-80000':
        case 'ghs-90000':
        case 'ghs-100000':
        case 'ghs-120000':
        case 'ghs-140000':
        case 'ghs-160000':
        case 'ghs-180000':
        case 'ghs-200000': {
          const keys = [
            'ghs-5000',
            'ghs-7500',
            'ghs-10000',
            'ghs-15000',
            'ghs-20000',
            'ghs-25000',
            'ghs-30000',
            'ghs-40000',
            'ghs-50000',
            'ghs-60000',
            'ghs-70000',
            'ghs-80000',
            'ghs-90000',
            'ghs-100000',
            'ghs-120000',
            'ghs-140000',
            'ghs-160000',
            'ghs-180000',
            'ghs-200000',
          ]
          keys.forEach((k) => {
            state.form['funeral-cover-options'][k] =
              k === data.id ? data.value : 'N'
          })
          dirty = true
          break
        }
        case 'term-ten':
          state.form['funeral-cover-options']['term-ten'] = data.value
          if (data.value === 'Y')
            state.form['funeral-cover-options']['term-fifteen'] = 'N'
          dirty = true
          break
        case 'term-fifteen':
          state.form['funeral-cover-options']['term-fifteen'] = data.value
          if (data.value === 'Y')
            state.form['funeral-cover-options']['term-ten'] = 'N'
          dirty = true
          break
        case 'memorial-seven-day':
        case 'memorial-forty-day':
        case 'memorial-one-year':
          state.form['rider-options'][data.id] = data.value
          dirty = true
          break
        case 'double-accident-benefit':
          state.form['double-accident-benefit'] = data.value
          dirty = true
          break
      }
      break

    case 'family-members-age':
      state.local = state.local || {}
      if (data.id === 'age-of-member') {
        if (!state.local.ageLocked) {
          state.local['age-of-member'] = data.value
          dirty = true
        }
      } else if (data.id === 'gender') {
        if (!state.local.genderLocked) {
          state.local.gender = data.value
          dirty = true
        }
      } else if (data.id === 'funeral-cover') {
        state.local['funeral-cover'] = data.value
        dirty = true
      } else if (data.id === 'transition-cover') {
        if (state.form['transition-plan']) {
          state.local['transition-cover'] = data.value
          dirty = true
        }
      } else if (data.id === 'old-age-cover') {
        if (eligibleForOldAgeCover(state.form, state.local.relationship)) {
          state.local['old-age-cover'] = data.value
          dirty = true
        }
      }
      break

    case 'family-members-quotation-details':
      state.local = state.local || {}
      ;[
        'funeral-cover-benefit',
        'funeral-cover-term',
        'memorial-seven-day',
        'memorial-forty-day',
        'memorial-one-year',
        'old-age-cover-benefit',
      ].forEach((k) => {
        if (data.id === k) {
          state.local[k] = data.value
          dirty = true
        }
      })
      if (data.id === 'additional-life-term-ten') {
        state.local['funeral-cover-term'] = data.value === 'Y' ? '10' : null
        dirty = true
      } else if (data.id === 'additional-life-term-fifteen') {
        state.local['funeral-cover-term'] = data.value === 'Y' ? '15' : null
        dirty = true
      }
      break
    case 'personal-details': {
      const detailSections = [
        'life-assured-identification',
        'life-assured-contact-details',
        'life-assured-residential-address',
        'life-assured-postal-address',
        'life-assured-digital-address',
        'life-assured-employment-details',
      ]
      detailSections.forEach((sectionKey) => {
        const template = form[sectionKey]
        if (!template || typeof template !== 'object') {
          return
        }
        if (
          !state.form[sectionKey] ||
          typeof state.form[sectionKey] !== 'object'
        ) {
          state.form[sectionKey] = { ...template }
        } else {
          Object.keys(state.form[sectionKey]).forEach((p) => {
            if (!(p in template)) {
              delete state.form[sectionKey][p]
            }
          })
        }
      })

      const idChoices = [
        'nia-ghana-card',
        'passport',
        'voter-id',
        'dvla',
        'nhis',
        'ssnit',
      ]
      if (idChoices.indexOf(data.id) >= 0) {
        const ident = state.form['life-assured-identification']
        if (data.value === 'Y') {
          idChoices.forEach((k) => {
            ident[k] = k === data.id ? 'Y' : ''
          })
        } else {
          ident[data.id] = data.value
        }
        dirty = true
      } else {
        const personalFormSection = detailSections.find(
          (s) => data.id in state.form[s]
        )
        if (personalFormSection) {
          state.form[personalFormSection][data.id] = data.value
          dirty = true
        } else {
          console.warn(`No form element found for ${data.id} [${data.value}]`)
        }
      }
      if (data.id === 'cell-phone') {
        if (emptyPhoneNumber(data.value)) {
          state.form['life-assured-contact-details']['cell-phone'] = null
          state.local = state.local || {}
          state.local.clearFieldErrors = data.id
          dirty = true
        }
      }
      break
    }
    case 'spouse-details': {
      const spouseIdCheckboxKeys = [
        'nia-ghana-card',
        'passport',
        'voter-id',
        'dvla',
        'nhis',
        'ssnit',
      ]
      if (
        !state.form['spouse-identification'] ||
        typeof state.form['spouse-identification'] !== 'object'
      ) {
        state.form['spouse-identification'] = {}
      }
      const sid = state.form['spouse-identification']

      let spouseDirty = false
      if (spouseIdCheckboxKeys.indexOf(data.id) >= 0) {
        if (data.value === 'Y') {
          spouseIdCheckboxKeys.forEach((k) => {
            sid[k] = k === data.id ? 'Y' : 'N'
          })
        } else {
          sid[data.id] = data.value
        }
        if (sid['nia-ghana-card'] === 'Y') {
          sid['id-type'] = 'National ID'
        } else if (sid.passport === 'Y') {
          sid['id-type'] = 'Passport'
        } else if (sid['voter-id'] === 'Y') {
          sid['id-type'] = 'Voters ID'
        } else if (sid.dvla === 'Y') {
          sid['id-type'] = 'Drivers New ID'
        } else if (sid.nhis === 'Y') {
          sid['id-type'] = 'Nhis'
        } else if (sid.ssnit === 'Y') {
          sid['id-type'] = 'Ssnit'
        } else {
          sid['id-type'] = null
        }
        spouseDirty = true
      }

      if (!spouseDirty) {
        ;[
          'spouse-identification',
          'spouse-contact-details',
          'spouse-employment-details',
        ].forEach((section) => {
          if (!state.form[section] || typeof state.form[section] !== 'object') {
            state.form[section] = {}
          }
          if (data.id in state.form[section]) {
            state.form[section][data.id] = data.value
            dirty = true
          }
        })
      }
      if (spouseDirty) {
        dirty = true
      }
      if (
        data.id === 'employer-organisation' ||
        data.id === 'employee-number' ||
        data.id === 'occupation'
      ) {
        if (
          !state.form['spouse-employment-details'] ||
          typeof state.form['spouse-employment-details'] !== 'object'
        ) {
          state.form['spouse-employment-details'] = {}
        }
        state.form['spouse-employment-details'][data.id] = data.value
        dirty = true
      }
      if (data.id === 'spouse-is-trustee') {
        state.form[data.id] = data.value
        dirty = true
      }
      if (data.id === 'spouse-funeral') {
        state.form[data.id] = data.value
        dirty = true
      }

      if (state.form['spouse-is-trustee'] === 'Y') {
        ;['identification', 'contact-details'].forEach((section) => {
          const spouseKey = `spouse-${section}`
          const trusteeKey = `trustee-${section}`
          if (
            state.form[spouseKey] &&
            typeof state.form[spouseKey] === 'object' &&
            state.form[trusteeKey] &&
            typeof state.form[trusteeKey] === 'object'
          ) {
            Object.keys(state.form[spouseKey]).forEach((field) => {
              state.form[trusteeKey][field] = state.form[spouseKey][field]
            })
          }
        })
        state.form['trustee-identification'].relationship = 'Spouse'
        dirty = true
      }
      break
    }
    case 'add-other-lives':
    case 'edit-other-lives':
      state.local = state.local || {}
      state.local[data.id] = data.value
      dirty = true
      break
    case 'additional-lives': {
      const idParts = data.id.split('-')
      const ix = parseInt(idParts[idParts.length - 1], 10)
      if (!isNaN(ix) && ix < (state.form['additional-lives'] || []).length) {
        idParts.pop()
        const fld = idParts.join('-')
        if (fld in state.form['additional-lives'][ix]) {
          state.form['additional-lives'][ix][fld] = data.value
          dirty = true
        } else {
          console.warn(`No form element found for ${data.id} [${data.value}]`)
        }
      }
      break
    }

    case 'trustee-details':
      ;['trustee-identification', 'trustee-contact-details'].forEach((key) => {
        const template = form[key]
        if (!template || typeof template !== 'object') {
          return
        }
        if (!state.form[key] || typeof state.form[key] !== 'object') {
          state.form[key] = { ...template }
        } else {
          Object.keys(template).forEach((p) => {
            if (!(p in state.form[key])) {
              state.form[key][p] = template[p]
            }
          })
        }
      })
      ;['trustee-identification', 'trustee-contact-details'].forEach(
        (section) => {
          if (data.id in state.form[section]) {
            state.form[section][data.id] = data.value
            dirty = true
          }
        }
      )
      break
    case 'add-beneficiary':
    case 'edit-beneficiary':
    case 'update-beneficiary-split-percentage':
      state.local = state.local || {}
      state.local[data.id] = data.value
      dirty = true
      break

    case 'insurance-arrangements':
      if (data.id in state.form['existing-policies']) {
        state.form['existing-policies'][data.id] = data.value
        dirty = true
      }
      {
        const m = String(data.id || '').match(
          /^(company-name|existing-contract-id|sum-assured)-(\d+)$/
        )
        if (m) {
          const key = m[1]
          const ix = parseInt(m[2], 10)
          if (Number.isFinite(ix) && ix >= 1 && ix <= 3) {
            if (
              setExistingPolicyDetailsFieldValue(
                state.form,
                ix,
                key,
                data.value
              )
            ) {
              dirty = true
            }
          }
        }
      }
      break
    case 'medical-underwriting':
      if (data.id in state.form['life-assured-general-habits']) {
        state.form['life-assured-general-habits'][data.id] = data.value
        dirty = true
      }
      if (data.id in state.form['life-assured-health-details']) {
        state.form['life-assured-health-details'][data.id] = data.value
        dirty = true
      }
      if (data.id in state.form['medical-attendant-details']) {
        state.form['medical-attendant-details'][data.id] = data.value
        dirty = true
      }
      if (data.id in state.form['life-assured-medical-details']) {
        state.form['life-assured-medical-details'][data.id] = data.value
        dirty = true
      }
      break
    case 'medical-declaration':
      if (data.id in state.form['life-assured-health-details']) {
        state.form['life-assured-health-details'][data.id] = data.value
        dirty = true
      }
      if (data.id in state.form['medical-attendant-details']) {
        state.form['medical-attendant-details'][data.id] = data.value
        dirty = true
      }
      if (data.id in state.form['life-assured-medical-details']) {
        state.form['life-assured-medical-details'][data.id] = data.value
        dirty = true
      }
      break

    case 'health-descriptions':
      let id = data.id.split('-')
      const index = parseInt(id.pop())
      if (!isNaN(index) && index <= state.form['health-concerns'].length) {
        id = id.join('-')
        if (id in state.form['health-concerns'][index - 1]) {
          state.form['health-concerns'][index - 1][id] = data.value
          dirty = true
        } else {
          console.warn(`No form element found for ${data.id} [${data.value}]`)
        }
      }
      break
    case 'payment-details':
      switch (data.id) {
        case 'debit-order':
        case 'stop-order':
          state.form['payment-method-selection'][data.id] = data.value
          dirty = true
          break
        case 'monthly':
        case 'quarterly':
        case 'annual':
        case 'bi-annual':
          state.form['payment-frequency'][data.id] = data.value
          dirty = true
          break
        case 'myself':
        case 'my-spouse':
          state.form['payer-person'][data.id] = data.value
          dirty = true
          break
        case 'payment-start-date':
          const date = dayjs(data.value)
          state.form['payment-start-date'] = date.isValid()
            ? date.format('YYYYMMDD')
            : null
          dirty = true
          break
      }
      break
    case 'debit-order-details':
      if (data.id in state.form['payment-bank-details']) {
        state.form['payment-bank-details'][data.id] = data.value
        dirty = true
      } else if (data.id === 'interactive-canvas-html') {
        state.form['payer-signature'] = data.value
        dirty = true
      }
      break
    case 'stop-order-details':
      if (data.id in state.form['payment-employment-details']) {
        state.form['payment-employment-details'][data.id] = data.value
        dirty = true
        if (data.id === 'employee-number') {
          ensureForcesPayOfficeDetails(state)
          state.form['forces-pay-office-details']['svc-number'] = data.value
        }
      } else if (data.id === 'interactive-canvas-html') {
        state.form['payer-signature'] = data.value
        dirty = true
      }
      break

    case 'forces-pay-office-additional-information':
      ensureForcesPayOfficeDetails(state)
      if (
        data.id in state.form['forces-pay-office-details'] ||
        data.id === 'ecowas-id-number'
      ) {
        state.form['forces-pay-office-details'][data.id] = data.value
        dirty = true
      }
      break

    case 'payment-details-verification':
      if (data.id === 'interactive-canvas-html') {
        state.form['payer-signature'] = data.value
        dirty = true
      }
      break

    case 'upload-document-pages':
      const doc = data.id.match(/^upload-(\d+)$/)
      if (doc) {
        if (doc <= state.local.pages.length) {
          state.local.pages[doc - 1] = data.value
        } else {
          state.local.pages.push(data.value)
        }
        dirty = true
      } else {
        console.warn(
          `No form element found for ${data.id} [${data.value}] in step ${state.step}`
        )
      }
      break

    case 'acceptance-screen':
      if (data.id === 'interactive-canvas-html') {
        state.form['acceptance-signature'] = data.value
      }
      break
  }
  if (dirty) {
    setPageItemFromState(state)
  }
}

const processItemClick = (state, data, asyncDispatch) => {
  let dirty = false
  switch (state.step) {
    case 'beneficiary-details':
      if (
        data.index > 0 &&
        data.index <= state.form['beneficiary-details'].length
      ) {
        changePage(state, 'edit-beneficiary')
        state.local = Object.assign(
          {},
          original(state.form['beneficiary-details'][data.index - 1])
        )
        state.local.index = data.index
        dirty = true
      }
      break
    case 'quotation-family':
      if (
        data.index > 0 &&
        data.index <= state.form['additional-lives'].length
      ) {
        gotoEditAdditionalLife(state, data.index - 1)
        dirty = true
      }
      break
    case 'other-lives-assured':
      if (
        data.index > 0 &&
        data.index <= state.form['additional-lives'].length
      ) {
        gotoEditAdditionalLife(state, data.index - 1)
        dirty = true
      }
      break
    case 'spouse-details':
      if (
        data.index > 0 &&
        data.index <= state.form['additional-lives'].length
      ) {
        gotoEditAdditionalLife(state, data.index - 1)
        dirty = true
      }
      break
  }
  if (dirty) {
    setPageItemFromState(state)
  }
}

export default {
  createState,
  processButton,
  processChange,
  processItemClick,
}
